import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import HubIcon from '@mui/icons-material/Hub';
import InsightsIcon from '@mui/icons-material/Insights';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ScienceIcon from '@mui/icons-material/Science';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import ViewListIcon from '@mui/icons-material/ViewList';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { toast } from 'react-toastify';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import PageBackButton from '../../../components/PageBackButton';
import { ErrorAlert } from '../common';
import { AgentAutocomplete } from '../common/AsyncEntitySelect';
import AIBuilder from '../system/AIBuilder';
import {
  agentApi,
  modelLabAssetApi,
  modelLabDatasetVersionApi,
  modelLabDeploymentApi,
  modelLabFeatureSetApi,
  modelLabTrainingJobApi,
  modelLabVersionApi,
} from '../../services/api';
import useModelLabTrainingJobsSocket from '../../hooks/useModelLabTrainingJobsSocket';
import useModelLabEventsSocket from '../../hooks/useModelLabEventsSocket';
import ModelLabPredictionTestingPanel from './ModelLabPredictionTestingPanel';
import { ComputeSpecSelector } from './ComputeSpecSelector';
import NeuralNetwork3D, { type Layer3DSpec } from './NeuralNetwork3D';
import {
  TrainingJobDetailsDialog,
  TrainingJobMonitorCard,
} from './TrainingJobMonitor';
import type {
  DataProfileColumn,
  ModelLabAsset,
  ModelLabAssetCreate,
  DatasetAnalysisReport,
  ModelLabDataProcessingQueued,
  ModelLabDatasetVersion,
  ModelLabDeployment,
  ModelLabFeatureSet,
  ModelLabFamily,
  ModelLabFramework,
  ModelLabProblemType,
  ModelLabTrainingJob,
  ModelLabTrainingJobCreate,
  ModelLabVersion,
} from '../../types';

const isQueuedDataProcessing = (value: unknown): value is ModelLabDataProcessingQueued => (
  Boolean(value && typeof value === 'object' && 'data_processing_job' in value)
);
import {
  deploymentStatusColor,
  formatMetricSummary,
  getApiErrorMessage,
  modelStageColor,
  parseCommaList,
  parseJsonInput,
} from './utils';

type ArchitectureMode = 'single' | 'voting' | 'stacking';
type CanvasView = 'model' | 'pipeline';
type BuilderOperation = 'training' | 'jobs' | 'review' | 'versions' | 'testing' | 'inference';
type BuilderWorkspaceTab = 'canvas' | 'analysis' | 'three_d' | 'code';

interface PreprocessingStep {
  id: string;
  type: string;
  label: string;
  params: Record<string, string>;
}

interface EstimatorStep {
  id: string;
  algorithm: string;
  params: Record<string, string>;
}

interface ModelStackState {
  preprocessing: PreprocessingStep[];
  mode: ArchitectureMode;
  singleEstimator: EstimatorStep;
  votingStrategy: 'hard' | 'soft';
  baseEstimators: EstimatorStep[];
  metaLearner: EstimatorStep;
}

interface BuilderFormState {
  name: string;
  description: string;
  definitionMode: 'stack' | 'code';
  family: ModelLabFamily;
  framework: ModelLabFramework;
  problemType: ModelLabProblemType;
  tags: string;
  executor: string;
  builderGoal: string;
  inputShape: string;
  targetName: string;
  featureColumns: string;
  objectiveMetric: string;
  evaluationMetrics: string;
  validationStrategy: string;
  customEntrypoint: string;
  customAllowedLibraries: string;
  customCode: string;
  outputSchemaJson: string;
  notes: string;
  runtimeOverridesJson: string;
}

interface LifecycleState {
  training: {
    datasetStrategy: string;
    featureSetStrategy: string;
    splitStrategy: string;
    trainSplit: string;
    validationSplit: string;
    testSplit: string;
    cvFolds: string;
    randomState: string;
    epochs: string;
    batchSize: string;
    learningRate: string;
    earlyStoppingPatience: string;
    computeTarget: string;
    launchPolicy: string;
    tuningStrategy: string;
  };
  review: {
    primaryMetric: string;
    successThreshold: string;
    secondaryMetrics: string;
    baselineName: string;
    comparisonMode: string;
    resultViews: string;
    explainability: string;
    reviewCadence: string;
    approvalPolicy: string;
  };
  versions: {
    initialStage: string;
    namingPattern: string;
    promotionPolicy: string;
    approvalGate: string;
    retentionPolicy: string;
    rollbackPolicy: string;
    artifactPolicy: string;
    lineageNotes: string;
  };
  testing: {
    dataValidation: string;
    unitTests: string;
    backtestWindow: string;
    shadowMode: string;
    canaryPercent: string;
    loadTestQps: string;
    samplePayloadJson: string;
    acceptanceChecklist: string;
  };
  inference: {
    servingMode: string;
    environment: string;
    endpointName: string;
    featureRecipe: string;
    minReplicas: string;
    maxReplicas: string;
    latencyBudgetMs: string;
    predictionLogging: string;
    monitoringPolicy: string;
  };
}

interface AlgorithmOption {
  value: string;
  label: string;
  tasks: Array<'classification' | 'regression' | 'time_series' | 'reinforcement'>;
  framework: ModelLabFramework;
  family: ModelLabFamily;
  defaults: Record<string, string>;
}

interface StackNodeData extends Record<string, unknown> {
  label: string;
  caption: string;
  tone: 'input' | 'prep' | 'layer' | 'model' | 'ensemble' | 'meta' | 'output';
  sourceId?: string;
  selected?: boolean;
  details?: string[];
}

interface CanvasNode {
  id: string;
  position: { x: number; y: number };
  selected?: boolean;
  data: StackNodeData;
}

interface CanvasEdge {
  id: string;
  source: string;
  target: string;
}

interface CreateAssetAction {
  operation?: BuilderOperation;
}

interface BuilderTrainingFormState {
  datasetVersionId: string;
  featureSetId: string;
  targetColumn: string;
  featureColumns: string;
  executor: string;
  computeSpecId: number | null;
  testSize: string;
  randomState: string;
  cvFolds: string;
}

const initialBuilderTrainingForm: BuilderTrainingFormState = {
  datasetVersionId: '',
  featureSetId: '',
  targetColumn: 'target',
  featureColumns: '',
  executor: 'cpu',
  computeSpecId: null,
  testSize: '0.25',
  randomState: '42',
  cvFolds: '0',
};

const createBlockId = () => `ml_${Math.random().toString(36).slice(2, 9)}`;
const createEmptyEstimator = (id: string): EstimatorStep => ({
  id,
  algorithm: '',
  params: {},
});

const ALGORITHMS: AlgorithmOption[] = [
  {
    value: 'logistic_regression',
    label: 'Logistic Regression',
    tasks: ['classification'],
    family: 'ML',
    framework: 'SKLEARN',
    defaults: { C: '1.0', max_iter: '250' },
  },
  {
    value: 'mlp',
    label: 'Neural Network (MLP)',
    tasks: ['classification', 'regression'],
    family: 'DL',
    framework: 'PYTORCH',
    defaults: { hidden_layer_sizes: '128,64', dropout: '0.2', activation: 'relu' },
  },
  {
    value: 'random_forest',
    label: 'Random Forest',
    tasks: ['classification', 'regression'],
    family: 'ML',
    framework: 'SKLEARN',
    defaults: { n_estimators: '200', max_depth: '12' },
  },
  {
    value: 'gradient_boosting',
    label: 'Gradient Boosting',
    tasks: ['classification', 'regression'],
    family: 'ML',
    framework: 'SKLEARN',
    defaults: { n_estimators: '160', learning_rate: '0.05', max_depth: '4' },
  },
  {
    value: 'xgboost',
    label: 'XGBoost',
    tasks: ['classification', 'regression'],
    family: 'ML',
    framework: 'CUSTOM',
    defaults: { n_estimators: '250', learning_rate: '0.04', max_depth: '6' },
  },
  {
    value: 'lightgbm',
    label: 'LightGBM',
    tasks: ['classification', 'regression'],
    family: 'ML',
    framework: 'CUSTOM',
    defaults: { n_estimators: '250', learning_rate: '0.04', num_leaves: '31' },
  },
  {
    value: 'ridge',
    label: 'Ridge Regression',
    tasks: ['regression'],
    family: 'ML',
    framework: 'SKLEARN',
    defaults: { alpha: '1.0' },
  },
  {
    value: 'lstm',
    label: 'LSTM',
    tasks: ['classification', 'regression', 'time_series'],
    family: 'DL',
    framework: 'PYTORCH',
    defaults: { hidden_size: '64', num_layers: '2', dropout: '0.2' },
  },
  {
    value: 'gru',
    label: 'GRU',
    tasks: ['classification', 'regression', 'time_series'],
    family: 'DL',
    framework: 'PYTORCH',
    defaults: { hidden_size: '64', num_layers: '2', dropout: '0.2' },
  },
  {
    value: 'transformer_encoder',
    label: 'Transformer Encoder',
    tasks: ['classification', 'regression', 'time_series'],
    family: 'DL',
    framework: 'PYTORCH',
    defaults: { d_model: '128', nhead: '4', num_layers: '3', dropout: '0.1' },
  },
  {
    value: 'dqn_policy',
    label: 'DQN Policy',
    tasks: ['reinforcement'],
    family: 'RL',
    framework: 'PYTORCH',
    defaults: { gamma: '0.99', learning_rate: '0.0003', replay_size: '50000' },
  },
];

const PREPROCESSING_STEPS = [
  { value: 'standard_scaler', label: 'Standard Scaler', params: {} },
  { value: 'robust_scaler', label: 'Robust Scaler', params: {} },
  { value: 'impute_missing', label: 'Impute Missing', params: { strategy: 'median' } },
  { value: 'pca', label: 'PCA', params: { n_components: '12' } },
  { value: 'select_k_best', label: 'SelectKBest', params: { k: '20' } },
  { value: 'sequence_windowing', label: 'Sequence Windowing', params: { window_size: '48', stride: '1' } },
];

const VALIDATION_STRATEGY_OPTIONS = [
  { value: 'holdout', label: 'Holdout' },
  { value: 'k_fold', label: 'K-fold' },
  { value: 'rolling_window', label: 'Rolling window' },
  { value: 'walk_forward', label: 'Walk-forward' },
  { value: 'episode_eval', label: 'Episode eval' },
];

const VALIDATION_STRATEGY_ALIASES: Record<string, string> = {
  train_validation_holdout: 'holdout',
  validation_holdout: 'holdout',
  hold_out: 'holdout',
  'hold-out': 'holdout',
  kfold: 'k_fold',
  'k-fold': 'k_fold',
  k_fold_cv: 'k_fold',
  cross_validation: 'k_fold',
  rolling: 'rolling_window',
  rolling_window_cv: 'rolling_window',
  walkforward: 'walk_forward',
  'walk-forward': 'walk_forward',
  walk_forward_validation: 'walk_forward',
  time_series_split: 'walk_forward',
  temporal_holdout: 'walk_forward',
  episode: 'episode_eval',
  episode_validation: 'episode_eval',
};

const normalizeValidationStrategy = (value: unknown, fallback = 'holdout') => {
  const raw = String(value || '').trim().toLowerCase();
  const normalized = VALIDATION_STRATEGY_ALIASES[raw] || raw;
  return VALIDATION_STRATEGY_OPTIONS.some((option) => option.value === normalized)
    ? normalized
    : fallback;
};

const initialForm: BuilderFormState = {
  name: '',
  description: '',
  definitionMode: 'stack',
  family: 'DL',
  framework: 'PYTORCH',
  problemType: 'CLASSIFICATION',
  tags: 'model-lab, builder',
  executor: 'cpu',
  builderGoal: 'Predict the target column from a curated feature stack.',
  inputShape: 'tabular',
  targetName: 'target',
  featureColumns: '',
  objectiveMetric: 'accuracy',
  evaluationMetrics: 'accuracy, f1_weighted, recall_weighted',
  validationStrategy: 'holdout',
  customEntrypoint: '',
  customAllowedLibraries: '',
  customCode: '',
  outputSchemaJson: '{\n  "prediction": "number",\n  "confidence": "number"\n}',
  notes: '',
  runtimeOverridesJson: '{}',
};

const initialStack: ModelStackState = {
  preprocessing: [],
  mode: 'single',
  singleEstimator: createEmptyEstimator('single_estimator'),
  votingStrategy: 'soft',
  baseEstimators: [],
  metaLearner: createEmptyEstimator('meta_lr'),
};

const initialLifecycle: LifecycleState = {
  training: {
    datasetStrategy: 'latest_approved_dataset_version',
    featureSetStrategy: 'use_builder_feature_pipeline',
    splitStrategy: 'train_validation_holdout',
    trainSplit: '0.70',
    validationSplit: '0.15',
    testSplit: '0.15',
    cvFolds: '0',
    randomState: '42',
    epochs: '50',
    batchSize: '64',
    learningRate: '0.001',
    earlyStoppingPatience: '8',
    computeTarget: 'cpu',
    launchPolicy: 'manual_after_review',
    tuningStrategy: 'single_run_then_hp_tuning',
  },
  review: {
    primaryMetric: 'accuracy',
    successThreshold: '0.70',
    secondaryMetrics: 'f1_weighted, precision_weighted, recall_weighted',
    baselineName: 'current_production_or_last_approved',
    comparisonMode: 'compare_against_baseline_and_last_run',
    resultViews: 'loss_curve, confusion_matrix, roc_curve, feature_importance',
    explainability: 'feature_importance_and_shap_sample',
    reviewCadence: 'after_each_training_job',
    approvalPolicy: 'manual_admin_approval_required',
  },
  versions: {
    initialStage: 'DRAFT',
    namingPattern: 'v{major}.{minor}.{patch}-{dataset_version}',
    promotionPolicy: 'promote_if_review_passes_thresholds',
    approvalGate: 'require_metrics_and_holdout_review',
    retentionPolicy: 'keep_last_10_versions_archive_rest',
    rollbackPolicy: 'rollback_to_previous_production_on_health_failure',
    artifactPolicy: 'store_model_artifact_metrics_feature_schema_lineage',
    lineageNotes: 'Track dataset version, feature set, training job, and builder stack snapshot.',
  },
  testing: {
    dataValidation: 'schema_null_range_distribution_checks',
    unitTests: 'feature_transform_and_predict_contract_tests',
    backtestWindow: 'last_90_days_or_latest_holdout_window',
    shadowMode: 'shadow_predictions_before_production',
    canaryPercent: '10',
    loadTestQps: '25',
    samplePayloadJson: '{\n  "open": 1.0,\n  "high": 1.1,\n  "low": 0.9,\n  "volume": 1000\n}',
    acceptanceChecklist: 'schema valid, metrics pass, prediction shape valid, no leakage warnings',
  },
  inference: {
    servingMode: 'batch_and_online_endpoint',
    environment: 'development',
    endpointName: 'model-lab-endpoint',
    featureRecipe: 'copy_feature_recipe_from_feature_set',
    minReplicas: '1',
    maxReplicas: '3',
    latencyBudgetMs: '500',
    predictionLogging: 'log_inputs_outputs_latency_and_version',
    monitoringPolicy: 'health_drift_prediction_logs_and_rollback_alerts',
  },
};

const NODE_TONES: Record<StackNodeData['tone'], { color: string; bg: string }> = {
  input: { color: '#38bdf8', bg: 'rgba(56, 189, 248, 0.12)' },
  prep: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)' },
  layer: { color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.12)' },
  model: { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.12)' },
  ensemble: { color: '#a78bfa', bg: 'rgba(167, 139, 250, 0.12)' },
  meta: { color: '#f472b6', bg: 'rgba(244, 114, 182, 0.12)' },
  output: { color: '#14b8a6', bg: 'rgba(20, 184, 166, 0.12)' },
};

const taskForProblem = (problemType: ModelLabProblemType): AlgorithmOption['tasks'][number] => {
  if (problemType === 'REGRESSION') return 'regression';
  if (problemType === 'TIME_SERIES') return 'time_series';
  if (problemType === 'REINFORCEMENT') return 'reinforcement';
  return 'classification';
};

const getAlgorithm = (value: string) => ALGORITHMS.find((algorithm) => algorithm.value === value) || ALGORITHMS[1];

const getAlgorithmLabel = (value: string) => getAlgorithm(value).label;

const isBuilderArchitectureMode = (value: unknown): value is ArchitectureMode => (
  value === 'single' || value === 'voting' || value === 'stacking'
);

const normalizeAlgorithmValue = (value: unknown): string | null => {
  if (typeof value !== 'string' || value.trim().length === 0) return null;
  const direct = ALGORITHMS.find((algorithm) => algorithm.value === value);
  if (direct) return direct.value;

  const normalized = value.toLowerCase().replace(/[^a-z0-9]/g, '');
  const aliases: Record<string, string> = {
    transformer: 'transformer_encoder',
    transformerencoder: 'transformer_encoder',
    pytorchtransformer: 'transformer_encoder',
    pytorchtransformerencoder: 'transformer_encoder',
    lstm: 'lstm',
    longshorttermmemory: 'lstm',
    gru: 'gru',
    gatedrecurrentunit: 'gru',
    mlp: 'mlp',
    neuralnetwork: 'mlp',
    feedforward: 'mlp',
    dense: 'mlp',
    randomforest: 'random_forest',
    gradientboosting: 'gradient_boosting',
    xgboost: 'xgboost',
    lightgbm: 'lightgbm',
    logisticregression: 'logistic_regression',
    ridge: 'ridge',
    dqn: 'dqn_policy',
    dqnpolicy: 'dqn_policy',
  };
  return aliases[normalized] || null;
};

const parameterSummary = (params: Record<string, string>) =>
  Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join(', ');

const parsePositiveNumbers = (value: string, fallback: number[]) => {
  const parsed = String(value || '')
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isFinite(item) && item > 0);

  return parsed.length ? parsed : fallback;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const toNumber = (value: string, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const CANVAS_MIN_ZOOM = 0.1;
const CANVAS_MAX_ZOOM = 5;
const CANVAS_ZOOM_STEP = 0.1;
const CHAT_PANE_MIN_WIDTH = 300;
const INSPECTOR_PANE_MIN_WIDTH = 300;
const CANVAS_PANE_MIN_WIDTH = 360;
const BUILDER_HANDLE_TOTAL_WIDTH = 16;

const getBalancedChatPaneWidth = (shellWidth: number, isInspectorOpen: boolean, inspectorPaneWidth: number) => {
  const totalHandleWidth = isInspectorOpen ? BUILDER_HANDLE_TOTAL_WIDTH : 8;
  const reservedInspectorWidth = isInspectorOpen ? inspectorPaneWidth : 0;
  const usableWidth = Math.max(
    CHAT_PANE_MIN_WIDTH + CANVAS_PANE_MIN_WIDTH,
    shellWidth - reservedInspectorWidth - totalHandleWidth,
  );
  const maxChatWidth = Math.max(CHAT_PANE_MIN_WIDTH, usableWidth - CANVAS_PANE_MIN_WIDTH);

  return clamp(usableWidth / 2, CHAT_PANE_MIN_WIDTH, maxChatWidth);
};

const MODEL_LAB_REALTIME_REFRESH_TOOLS = [
  'create_dataset',
  'delete_dataset',
  'import_market_data',
  'upload_dataset_csv',
  'merge_dataset_versions',
  'create_feature_set',
  'update_feature_set',
  'delete_feature_set',
  'create_model_asset',
  'update_model_asset',
  'delete_model_asset',
  'promote_model_version',
  'demote_model_version',
  'create_training_job',
  'launch_training_job',
  'cancel_training_job',
  'create_deployment',
  'activate_deployment',
  'pause_deployment',
  'run_prediction',
  'rollback_deployment',
  'delete_deployment',
  'auto_train_model',
  'predict_live',
  'create_hp_tuning_job',
  'launch_hp_tuning_job',
  'create_automl_run',
  'launch_automl_run',
  'create_explainability_report',
  'compute_explainability',
  'create_model_monitor',
  'run_drift_check',
  'create_rl_training_job',
  'launch_rl_training_job',
];

const getFeatureCount = (featureColumns: string) => {
  const count = parseCommaList(featureColumns).length;
  return count > 0 ? count : 16;
};

const hasConfiguredEstimator = (estimator: EstimatorStep | null | undefined) => Boolean(estimator?.algorithm);

const hasSerializedArchitecture = (stack: ModelStackState) => (
  stack.preprocessing.length > 0 ||
  hasConfiguredEstimator(stack.singleEstimator) ||
  stack.baseEstimators.some(hasConfiguredEstimator) ||
  hasConfiguredEstimator(stack.metaLearner)
);

const getOutputUnits = (problemType: ModelLabProblemType) => {
  if (problemType === 'CLASSIFICATION') return 2;
  if (problemType === 'REINFORCEMENT') return 4;
  return 1;
};

const buildEstimatorLayers = (
  estimator: EstimatorStep,
  problemType: ModelLabProblemType,
): Layer3DSpec[] => {
  const algorithm = getAlgorithm(estimator.algorithm);
  const params = estimator.params || {};

  if (estimator.algorithm === 'mlp') {
    return parsePositiveNumbers(params.hidden_layer_sizes, [128, 64]).map((units, index) => ({
      label: `Dense ${index + 1}`,
      units,
      color: '#22c55e',
      kind: 'dense',
      detail: `${units} neurons - ${params.activation || 'relu'}`,
    }));
  }

  const toNumber = (value: string, fallback: number) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  if (estimator.algorithm === 'lstm' || estimator.algorithm === 'gru') {
    const units = Number(params.hidden_size) || 64;
    const count = Number(params.num_layers) || 2;
    const recurrentLayers = Array.from({ length: count }, (_, index) => ({
      label: `${algorithm.label} ${index + 1}`,
      units,
      color: '#22c55e',
      kind: 'recurrent',
      detail: `hidden ${units} - dropout ${params.dropout || '0'}`,
    }));
    const denseHeadLayers = parsePositiveNumbers(params.head_layer_sizes, []).map((headUnits, index) => ({
      label: `Dense Head ${index + 1}`,
      units: headUnits,
      color: '#22c55e',
      kind: 'dense',
      detail: `${headUnits} neurons - ${params.head_activation || params.activation || 'relu'}`,
    }));
    return [...recurrentLayers, ...denseHeadLayers];
  }

  if (estimator.algorithm === 'transformer_encoder') {
    const dModel = Number(params.d_model) || 128;
    const layerCount = Number(params.num_layers) || 3;
    const feedForward = Number(params.dim_feedforward) || dModel * 4;
    const transformerLayers = Array.from({ length: layerCount }).flatMap((_, index) => [
      {
        label: `Attention ${index + 1}`,
        units: dModel,
        color: '#8b5cf6',
        kind: 'attention',
        detail: `d_model ${dModel} - heads ${params.nhead || '4'}`,
      },
      {
        label: `Feed Forward ${index + 1}`,
        units: feedForward,
        color: '#a78bfa',
        kind: 'feed_forward',
        detail: `dim ${feedForward}`,
      },
    ]);
    const denseHeadLayers = parsePositiveNumbers(params.head_layer_sizes, []).map((headUnits, index) => ({
      label: `Dense Head ${index + 1}`,
      units: headUnits,
      color: '#22c55e',
      kind: 'dense',
      detail: `${headUnits} neurons - ${params.head_activation || params.activation || 'relu'}`,
    }));
    return [...transformerLayers, ...denseHeadLayers];
  }

  if (estimator.algorithm === 'dqn_policy') {
    return [
      { label: 'Policy Hidden 1', units: 128, color: '#22c55e', kind: 'dense', detail: 'state encoder' },
      { label: 'Policy Hidden 2', units: 64, color: '#22c55e', kind: 'dense', detail: `gamma ${params.gamma || '0.99'}` },
      { label: 'Q Values', units: getOutputUnits(problemType), color: '#14b8a6', kind: 'q_values', detail: 'action scores' },
    ];
  }

  if (['random_forest', 'gradient_boosting', 'xgboost', 'lightgbm'].includes(estimator.algorithm)) {
    const estimators = Math.min(Number(params.n_estimators) || 100, 64);
    const depth = Math.max(Number(params.max_depth) || 6, 2);
    return [
      {
        label: algorithm.label,
        units: estimators,
        color: '#22c55e',
        kind: 'tree_ensemble',
        detail: `${estimators} estimators - depth ${depth}`,
      },
      {
        label: 'Leaf Aggregation',
        units: Math.min(estimators * 2, 64),
        color: '#f59e0b',
        kind: 'aggregation',
        detail: 'vote / score aggregation',
      },
    ];
  }

  return [
    {
      label: algorithm.label,
      units: problemType === 'CLASSIFICATION' ? 8 : 4,
      color: '#22c55e',
      kind: 'linear_model',
      detail: parameterSummary(params) || 'linear decision layer',
    },
  ];
};

const buildStackVisualizationLayers = (
  form: BuilderFormState,
  stack: ModelStackState,
): Layer3DSpec[] => {
  if (!hasConfiguredEstimator(stack.singleEstimator) && stack.baseEstimators.length === 0) {
    return [];
  }

  const featureCount = getFeatureCount(form.featureColumns);
  const outputUnits = getOutputUnits(form.problemType);
  const layers: Layer3DSpec[] = [
    {
      label: 'Input Tensor',
      units: featureCount,
      color: '#38bdf8',
      kind: 'input',
      detail: `${featureCount} model features`,
    },
  ];

  if (stack.mode === 'single') {
    layers.push(...buildEstimatorLayers(stack.singleEstimator, form.problemType));
  } else {
    layers.push({
      label: stack.mode === 'voting' ? 'Base Model Stack' : 'Base Learner Stack',
      units: Math.max(stack.baseEstimators.length * 8, 8),
      color: '#8b5cf6',
      kind: 'ensemble',
      detail: stack.baseEstimators.map((estimator) => getAlgorithmLabel(estimator.algorithm)).join(', '),
    });

    if (stack.mode === 'stacking') {
      layers.push(...buildEstimatorLayers(stack.metaLearner, form.problemType));
    } else {
      layers.push({
        label: `${stack.votingStrategy.toUpperCase()} Vote`,
        units: Math.max(stack.baseEstimators.length, 2),
        color: '#a78bfa',
        kind: 'vote',
        detail: 'ensemble decision combiner',
      });
    }
  }

  layers.push({
    label: 'Prediction Head',
    units: outputUnits,
    color: '#14b8a6',
    kind: 'output',
    detail: `${form.problemType} - ${form.objectiveMetric}`,
  });

  return layers;
};

const serializeLifecyclePlan = (lifecycle: LifecycleState, form: BuilderFormState) => ({
  training: {
    dataset_strategy: lifecycle.training.datasetStrategy,
    feature_set_strategy: lifecycle.training.featureSetStrategy,
    split_strategy: lifecycle.training.splitStrategy,
    train_split: toNumber(lifecycle.training.trainSplit, 0.7),
    validation_split: toNumber(lifecycle.training.validationSplit, 0.15),
    test_split: toNumber(lifecycle.training.testSplit, 0.15),
    cv_folds: toNumber(lifecycle.training.cvFolds, 0),
    random_state: toNumber(lifecycle.training.randomState, 42),
    epochs: toNumber(lifecycle.training.epochs, 50),
    batch_size: toNumber(lifecycle.training.batchSize, 64),
    learning_rate: toNumber(lifecycle.training.learningRate, 0.001),
    early_stopping_patience: toNumber(lifecycle.training.earlyStoppingPatience, 8),
    compute_target: lifecycle.training.computeTarget,
    launch_policy: lifecycle.training.launchPolicy,
    tuning_strategy: lifecycle.training.tuningStrategy,
    target_column: form.targetName,
    feature_columns: parseCommaList(form.featureColumns),
  },
  review: {
    primary_metric: lifecycle.review.primaryMetric,
    success_threshold: toNumber(lifecycle.review.successThreshold, 0.7),
    secondary_metrics: parseCommaList(lifecycle.review.secondaryMetrics),
    baseline_name: lifecycle.review.baselineName,
    comparison_mode: lifecycle.review.comparisonMode,
    result_views: parseCommaList(lifecycle.review.resultViews),
    explainability: lifecycle.review.explainability,
    review_cadence: lifecycle.review.reviewCadence,
    approval_policy: lifecycle.review.approvalPolicy,
  },
  versions: {
    initial_stage: lifecycle.versions.initialStage,
    naming_pattern: lifecycle.versions.namingPattern,
    promotion_policy: lifecycle.versions.promotionPolicy,
    approval_gate: lifecycle.versions.approvalGate,
    retention_policy: lifecycle.versions.retentionPolicy,
    rollback_policy: lifecycle.versions.rollbackPolicy,
    artifact_policy: lifecycle.versions.artifactPolicy,
    lineage_notes: lifecycle.versions.lineageNotes,
  },
  testing: {
    data_validation: lifecycle.testing.dataValidation,
    unit_tests: lifecycle.testing.unitTests,
    backtest_window: lifecycle.testing.backtestWindow,
    shadow_mode: lifecycle.testing.shadowMode,
    canary_percent: toNumber(lifecycle.testing.canaryPercent, 10),
    load_test_qps: toNumber(lifecycle.testing.loadTestQps, 25),
    sample_payload: parseJsonInput(lifecycle.testing.samplePayloadJson, {}),
    acceptance_checklist: parseCommaList(lifecycle.testing.acceptanceChecklist),
  },
  inference: {
    serving_mode: lifecycle.inference.servingMode,
    environment: lifecycle.inference.environment,
    endpoint_name: lifecycle.inference.endpointName,
    feature_recipe: lifecycle.inference.featureRecipe,
    scaling: {
      min_replicas: toNumber(lifecycle.inference.minReplicas, 1),
      max_replicas: toNumber(lifecycle.inference.maxReplicas, 3),
    },
    latency_budget_ms: toNumber(lifecycle.inference.latencyBudgetMs, 500),
    prediction_logging: lifecycle.inference.predictionLogging,
    monitoring_policy: lifecycle.inference.monitoringPolicy,
  },
});

const PaneResizeHandle: React.FC<{ onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void }> = ({ onMouseDown }) => (
  <Box
    role="separator"
    aria-orientation="vertical"
    onMouseDown={onMouseDown}
    sx={{
      width: 8,
      cursor: 'col-resize',
      bgcolor: 'divider',
      opacity: 0.7,
      transition: 'opacity 120ms ease, background-color 120ms ease',
      '&:hover': {
        opacity: 1,
        bgcolor: 'primary.main',
      },
    }}
  />
);

const serializeEstimator = (estimator: EstimatorStep) => (
  hasConfiguredEstimator(estimator)
    ? {
        algorithm: estimator.algorithm,
        params: estimator.params,
      }
    : undefined
);

const serializeArchitecture = (stack: ModelStackState) => {
  if (!hasSerializedArchitecture(stack)) {
    return undefined;
  }

  const singleEstimator = serializeEstimator(stack.singleEstimator);
  const baseEstimators = stack.baseEstimators
    .map(serializeEstimator)
    .filter((estimator): estimator is { algorithm: string; params: Record<string, string> } => Boolean(estimator));
  const metaLearner = serializeEstimator(stack.metaLearner);

  return {
    preprocessing: stack.preprocessing.map((step) => ({
      type: step.type,
      label: step.label,
      params: step.params,
    })),
    model_type: stack.mode,
    ...(singleEstimator ? { single_estimator: singleEstimator } : {}),
    ...(stack.mode !== 'single' ? { voting_strategy: stack.votingStrategy } : {}),
    ...(baseEstimators.length > 0 ? { base_estimators: baseEstimators } : {}),
    ...(metaLearner ? { meta_learner: metaLearner } : {}),
  };
};

const toTextValue = (value: unknown, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const toJsonText = (value: unknown, fallback: string) => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
};

const toParamRecord = (params: unknown): Record<string, string> => {
  if (!params || typeof params !== 'object' || Array.isArray(params)) return {};
  return Object.entries(params as Record<string, unknown>).reduce<Record<string, string>>((acc, [key, value]) => {
    acc[key] = toTextValue(value);
    return acc;
  }, {});
};

const isArchitectureMode = isBuilderArchitectureMode;

const normalizeEstimator = (source: any, fallback: EstimatorStep, id: string): EstimatorStep => {
  const algorithm = typeof source?.algorithm === 'string' ? source.algorithm : fallback.algorithm;
  return {
    id: typeof source?.id === 'string' ? source.id : fallback.id || id,
    algorithm,
    params: {
      ...(algorithm ? getAlgorithm(algorithm).defaults : {}),
      ...toParamRecord(source?.params || source?.hyperparameters),
    },
  };
};

const getLayerText = (layer: any, keys: string[]) => {
  for (const key of keys) {
    const value = layer?.[key] ?? layer?.config?.[key] ?? layer?.params?.[key] ?? layer?.kwargs?.[key];
    if (value !== null && value !== undefined && value !== '') return String(value);
  }
  return '';
};

const getLayerType = (layer: any) => getLayerText(layer, [
  'type',
  'kind',
  'layer_type',
  'module',
  'class_name',
  'operation',
  'name',
  'label',
]).toLowerCase();

const getLayerNumber = (layer: any, keys: string[]) => {
  for (const key of keys) {
    const value = layer?.[key] ?? layer?.config?.[key] ?? layer?.params?.[key] ?? layer?.kwargs?.[key];
    const parsed = Number(Array.isArray(value) ? value[0] : value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
};

const getLayerArray = (...candidates: any[]) => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) return candidate;
  }
  return null;
};

const getExplicitArchitectureLayers = (
  runtimeSpec: Record<string, any>,
  authoring: Record<string, any>,
  noCode: Record<string, any>,
  architecture: any,
) => getLayerArray(
  Array.isArray(architecture) ? architecture : null,
  architecture?.layers,
  architecture?.model_stack?.layers,
  architecture?.network?.layers,
  architecture?.neural_network?.layers,
  architecture?.modules,
  noCode.layers,
  noCode.model_stack?.layers,
  noCode.neural_network?.layers,
  authoring.stack_builder?.layers,
  authoring.stack_builder?.model_stack?.layers,
  authoring.stack_builder?.neural_network?.layers,
  runtimeSpec.layers,
  runtimeSpec.model_stack?.layers,
  runtimeSpec.neural_network?.layers,
  runtimeSpec.pytorch?.layers,
);

const firstRecord = (...candidates: any[]) => candidates.find(
  (candidate) => candidate && typeof candidate === 'object' && !Array.isArray(candidate),
) || {};

const firstDefined = (...candidates: any[]) => candidates.find(
  (candidate) => candidate !== null && candidate !== undefined && candidate !== '',
);

const normalizeArchitectureRecord = (
  architecture: any,
  noCode: Record<string, any>,
  runtimeSpec: Record<string, any>,
): Record<string, any> => {
  if (architecture && typeof architecture === 'object' && !Array.isArray(architecture)) {
    if (
      isBuilderArchitectureMode(architecture.model_type) ||
      architecture.single_estimator ||
      architecture.base_estimators ||
      architecture.meta_learner ||
      Array.isArray(architecture.layers)
    ) {
      return architecture;
    }

    const algorithm = normalizeAlgorithmValue(firstDefined(
      architecture.algorithm,
      architecture.model,
      isBuilderArchitectureMode(architecture.model_type) ? undefined : architecture.model_type,
      architecture.type,
      architecture.name,
      architecture.class_name,
    ));
    if (algorithm) {
      return {
        ...architecture,
        model_type: 'single',
        single_estimator: {
          algorithm,
          params: firstRecord(
            architecture.params,
            architecture.hyperparameters,
            noCode.hyperparameters,
            runtimeSpec.hyperparameters,
          ),
        },
      };
    }

    return architecture;
  }

  const algorithm = normalizeAlgorithmValue(firstDefined(
    architecture,
    noCode.algorithm,
    noCode.model,
    noCode.model_type,
    noCode.model_architecture,
    runtimeSpec.algorithm,
    runtimeSpec.model,
  ));
  if (!algorithm) return {};

  return {
    model_type: 'single',
    single_estimator: {
      algorithm,
      params: firstRecord(
        noCode.hyperparameters,
        noCode.params,
        noCode.parameters,
        runtimeSpec.hyperparameters,
        runtimeSpec.params,
      ),
    },
  };
};

const isLinearLikeLayer = (layer: any) => {
  const type = getLayerType(layer);
  return type.includes('linear') || type.includes('dense') || type.includes('fully_connected') || /^fc\d*$/.test(type);
};

const isDropoutLayer = (layer: any) => getLayerType(layer).includes('dropout');

const getLinearUnits = (layer: any) => getLayerNumber(layer, [
  'out_features',
  'output_dim',
  'output_size',
  'units',
  'neurons',
  'features',
  'dim',
]);

const getActivationFromLayers = (layers: any[]) => {
  const activationLayer = layers.find((layer) => {
    const type = getLayerType(layer);
    return ['relu', 'gelu', 'tanh', 'sigmoid', 'elu', 'silu'].some((name) => type.includes(name));
  });
  return getLayerType(activationLayer) || 'relu';
};

const hydrateStackFromLayerList = (
  layers: any[],
  current: ModelStackState,
  problemType: ModelLabProblemType,
): ModelStackState | null => {
  const recurrentLayer = layers.find((layer) => {
    const type = getLayerType(layer);
    return type.includes('lstm') || type.includes('gru');
  });
  const transformerLayer = layers.find((layer) => getLayerType(layer).includes('transformer'));
  const linearLayers = layers.filter(isLinearLikeLayer);
  const linearUnits = linearLayers
    .map(getLinearUnits)
    .filter((units): units is number => typeof units === 'number');
  const hiddenLinearUnits = linearUnits.length > 1 ? linearUnits.slice(0, -1) : [];
  const dropout = layers.find(isDropoutLayer);
  const dropoutValue = getLayerNumber(dropout, ['p', 'rate', 'dropout', 'probability']);
  const activation = getActivationFromLayers(layers);

  if (recurrentLayer) {
    const type = getLayerType(recurrentLayer).includes('gru') ? 'gru' : 'lstm';
    const defaults = getAlgorithm(type).defaults;
    return {
      ...current,
      mode: 'single',
      singleEstimator: {
        id: current.singleEstimator.id || 'single_estimator',
        algorithm: type,
        params: {
          ...defaults,
          hidden_size: String(getLayerNumber(recurrentLayer, ['hidden_size', 'hidden_dim', 'units', 'output_size']) || defaults.hidden_size || 64),
          num_layers: String(getLayerNumber(recurrentLayer, ['num_layers', 'layers', 'depth']) || defaults.num_layers || 1),
          dropout: String(dropoutValue ?? recurrentLayer.dropout ?? defaults.dropout ?? '0'),
          head_layer_sizes: hiddenLinearUnits.join(','),
          head_activation: activation,
        },
      },
    };
  }

  if (transformerLayer) {
    const defaults = getAlgorithm('transformer_encoder').defaults;
    return {
      ...current,
      mode: 'single',
      singleEstimator: {
        id: current.singleEstimator.id || 'single_estimator',
        algorithm: 'transformer_encoder',
        params: {
          ...defaults,
          d_model: String(getLayerNumber(transformerLayer, ['d_model', 'hidden_size', 'embedding_dim', 'units']) || defaults.d_model || 128),
          nhead: String(getLayerNumber(transformerLayer, ['nhead', 'num_heads', 'heads']) || defaults.nhead || 4),
          num_layers: String(getLayerNumber(transformerLayer, ['num_layers', 'layers', 'depth']) || defaults.num_layers || 1),
          dropout: String(dropoutValue ?? transformerLayer.dropout ?? defaults.dropout ?? '0'),
          head_layer_sizes: hiddenLinearUnits.join(','),
          head_activation: activation,
        },
      },
    };
  }

  const mlpHiddenUnits = hiddenLinearUnits.length ? hiddenLinearUnits : linearUnits;
  if (mlpHiddenUnits.length) {
    return {
      ...current,
      mode: 'single',
      singleEstimator: {
        id: current.singleEstimator.id || 'single_estimator',
        algorithm: 'mlp',
        params: {
          ...getAlgorithm('mlp').defaults,
          hidden_layer_sizes: mlpHiddenUnits.join(','),
          dropout: String(dropoutValue ?? current.singleEstimator.params.dropout ?? '0.2'),
          activation,
          output_units: String(linearUnits[linearUnits.length - 1] || getOutputUnits(problemType)),
        },
      },
    };
  }

  return null;
};

const hydrateStackFromRuntimeSpec = (
  runtimeSpec: Record<string, any>,
  current: ModelStackState,
  problemType: ModelLabProblemType = 'CLASSIFICATION',
): ModelStackState => {
  const authoring = runtimeSpec.authoring || {};
  const noCode = {
    ...(authoring.no_code || {}),
    ...(authoring.stack_builder || {}),
  };
  const architecture = noCode.architecture || authoring.stack_builder?.architecture || runtimeSpec.architecture;
  const architectureRecord = normalizeArchitectureRecord(architecture, noCode, runtimeSpec);

  const preprocessing = Array.isArray(architectureRecord.preprocessing)
    ? architectureRecord.preprocessing.map((step: any, index: number) => {
        const stepType = toTextValue(step?.type, `step_${index + 1}`);
        const knownStep = PREPROCESSING_STEPS.find((item) => item.value === stepType);
        return {
          id: typeof step?.id === 'string' ? step.id : current.preprocessing[index]?.id || `prep_${stepType}_${index + 1}`,
          type: stepType,
          label: toTextValue(step?.label, knownStep?.label || stepType),
          params: toParamRecord(step?.params || knownStep?.params),
        };
      })
    : current.preprocessing;

  const explicitLayers = getExplicitArchitectureLayers(runtimeSpec, authoring, noCode, architecture);
  const explicitLayerStack = explicitLayers ? hydrateStackFromLayerList(explicitLayers, current, problemType) : null;
  if (explicitLayerStack) return { ...explicitLayerStack, preprocessing };

  if (Object.keys(architectureRecord).length === 0) return current;

  const mode = isArchitectureMode(architectureRecord.model_type) ? architectureRecord.model_type : current.mode;
  const simpleSingleEstimator = architectureRecord.single_estimator
    || (typeof architectureRecord.algorithm === 'string'
      ? {
          algorithm: architectureRecord.algorithm,
          params: architectureRecord.params || architectureRecord.hyperparameters,
        }
      : undefined);
  const baseEstimators = Array.isArray(architectureRecord.base_estimators) && architectureRecord.base_estimators.length > 0
    ? architectureRecord.base_estimators.map((estimator: any, index: number) => normalizeEstimator(
        estimator,
        current.baseEstimators[index] || initialStack.baseEstimators[index % initialStack.baseEstimators.length],
        `base_${index + 1}`,
      ))
    : current.baseEstimators;

  return {
    preprocessing,
    mode,
    singleEstimator: normalizeEstimator(simpleSingleEstimator, current.singleEstimator, 'single_estimator'),
    votingStrategy: architectureRecord.voting_strategy === 'hard' ? 'hard' : architectureRecord.voting_strategy === 'soft' ? 'soft' : current.votingStrategy,
    baseEstimators,
    metaLearner: normalizeEstimator(architectureRecord.meta_learner, current.metaLearner, 'meta_learner'),
  };
};

const hydrateLifecycleFromPlan = (plan: any, current: LifecycleState): LifecycleState => {
  if (!plan || typeof plan !== 'object') return current;
  const training = plan.training || {};
  const review = plan.review || {};
  const versions = plan.versions || {};
  const testing = plan.testing || {};
  const inference = plan.inference || {};
  const scaling = inference.scaling || {};

  return {
    training: {
      ...current.training,
      datasetStrategy: toTextValue(training.dataset_strategy, current.training.datasetStrategy),
      featureSetStrategy: toTextValue(training.feature_set_strategy, current.training.featureSetStrategy),
      splitStrategy: toTextValue(training.split_strategy, current.training.splitStrategy),
      trainSplit: toTextValue(training.train_split, current.training.trainSplit),
      validationSplit: toTextValue(training.validation_split, current.training.validationSplit),
      testSplit: toTextValue(training.test_split, current.training.testSplit),
      cvFolds: toTextValue(training.cv_folds, current.training.cvFolds),
      randomState: toTextValue(training.random_state, current.training.randomState),
      epochs: toTextValue(training.epochs, current.training.epochs),
      batchSize: toTextValue(training.batch_size, current.training.batchSize),
      learningRate: toTextValue(training.learning_rate, current.training.learningRate),
      earlyStoppingPatience: toTextValue(training.early_stopping_patience, current.training.earlyStoppingPatience),
      computeTarget: toTextValue(training.compute_target, current.training.computeTarget),
      launchPolicy: toTextValue(training.launch_policy, current.training.launchPolicy),
      tuningStrategy: toTextValue(training.tuning_strategy, current.training.tuningStrategy),
    },
    review: {
      ...current.review,
      primaryMetric: toTextValue(review.primary_metric, current.review.primaryMetric),
      successThreshold: toTextValue(review.success_threshold, current.review.successThreshold),
      secondaryMetrics: toTextValue(review.secondary_metrics, current.review.secondaryMetrics),
      baselineName: toTextValue(review.baseline_name, current.review.baselineName),
      comparisonMode: toTextValue(review.comparison_mode, current.review.comparisonMode),
      resultViews: toTextValue(review.result_views, current.review.resultViews),
      explainability: toTextValue(review.explainability, current.review.explainability),
      reviewCadence: toTextValue(review.review_cadence, current.review.reviewCadence),
      approvalPolicy: toTextValue(review.approval_policy, current.review.approvalPolicy),
    },
    versions: {
      ...current.versions,
      initialStage: toTextValue(versions.initial_stage, current.versions.initialStage),
      namingPattern: toTextValue(versions.naming_pattern, current.versions.namingPattern),
      promotionPolicy: toTextValue(versions.promotion_policy, current.versions.promotionPolicy),
      approvalGate: toTextValue(versions.approval_gate, current.versions.approvalGate),
      retentionPolicy: toTextValue(versions.retention_policy, current.versions.retentionPolicy),
      rollbackPolicy: toTextValue(versions.rollback_policy, current.versions.rollbackPolicy),
      artifactPolicy: toTextValue(versions.artifact_policy, current.versions.artifactPolicy),
      lineageNotes: toTextValue(versions.lineage_notes, current.versions.lineageNotes),
    },
    testing: {
      ...current.testing,
      dataValidation: toTextValue(testing.data_validation, current.testing.dataValidation),
      unitTests: toTextValue(testing.unit_tests, current.testing.unitTests),
      backtestWindow: toTextValue(testing.backtest_window, current.testing.backtestWindow),
      shadowMode: toTextValue(testing.shadow_mode, current.testing.shadowMode),
      canaryPercent: toTextValue(testing.canary_percent, current.testing.canaryPercent),
      loadTestQps: toTextValue(testing.load_test_qps, current.testing.loadTestQps),
      samplePayloadJson: toJsonText(testing.sample_payload, current.testing.samplePayloadJson),
      acceptanceChecklist: toTextValue(testing.acceptance_checklist, current.testing.acceptanceChecklist),
    },
    inference: {
      ...current.inference,
      servingMode: toTextValue(inference.serving_mode, current.inference.servingMode),
      environment: toTextValue(inference.environment, current.inference.environment),
      endpointName: toTextValue(inference.endpoint_name, current.inference.endpointName),
      featureRecipe: toTextValue(inference.feature_recipe, current.inference.featureRecipe),
      minReplicas: toTextValue(scaling.min_replicas, current.inference.minReplicas),
      maxReplicas: toTextValue(scaling.max_replicas, current.inference.maxReplicas),
      latencyBudgetMs: toTextValue(inference.latency_budget_ms, current.inference.latencyBudgetMs),
      predictionLogging: toTextValue(inference.prediction_logging, current.inference.predictionLogging),
      monitoringPolicy: toTextValue(inference.monitoring_policy, current.inference.monitoringPolicy),
    },
  };
};

const getDefinitionModeFromRuntime = (runtimeSpec: Record<string, any>, current: BuilderFormState['definitionMode']) => {
  if (runtimeSpec.definition_mode === 'code' || runtimeSpec.authoring_mode === 'custom_code' || runtimeSpec.authoring_mode === 'python_sandbox') {
    return 'code';
  }
  if (runtimeSpec.definition_mode === 'stack' || runtimeSpec.authoring_mode === 'model_stack_builder') {
    return 'stack';
  }
  return current;
};

const getAuthoringModeLabel = (form: BuilderFormState) => (
  form.definitionMode === 'code' ? 'Custom Python' : 'Model Stack Builder'
);

const getAuthoringModeHelper = (form: BuilderFormState) => (
  form.definitionMode === 'code'
    ? (form.customCode.trim() ? 'Custom code will be used for training/deployment.' : 'Custom code mode selected; add Python code before training.')
    : 'System-managed stack blocks will be serialized from the canvas.'
);

const getAgentModelLabel = (agent: any) => (
  agent?.model_name || agent?.model?.name || agent?.model?.model || 'Model not set'
);

const getAgentSelectorLabel = (agent: any) => (
  agent ? `${agent.name} · ${getAgentModelLabel(agent)}` : 'Select agent'
);

const BuilderInfoRow: React.FC<{ label: string; value: React.ReactNode; helper?: React.ReactNode }> = ({ label, value, helper }) => (
  <Box
    sx={{
      p: 1,
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
      minWidth: 0,
    }}
  >
    <Typography variant="caption" color="text.secondary" display="block" noWrap>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }} noWrap>
      {value}
    </Typography>
    {helper ? (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }} noWrap>
        {helper}
      </Typography>
    ) : null}
  </Box>
);

const EmptyOperationState: React.FC<{ title: string; message: string }> = ({ title, message }) => (
  <Alert severity="info" variant="outlined">
    <Typography variant="subtitle2">{title}</Typography>
    <Typography variant="body2">{message}</Typography>
  </Alert>
);

const TrainingJobCards: React.FC<{ jobs: ModelLabTrainingJob[] }> = ({ jobs }) => {
  const [selectedJob, setSelectedJob] = useState<ModelLabTrainingJob | null>(null);

  if (jobs.length === 0) {
    return (
      <EmptyOperationState
        title="No training jobs yet"
        message="Create and launch a job from Start Training to begin building versions for this model."
      />
    );
  }

  return (
    <>
      <Stack spacing={1}>
        {jobs.map((job) => (
          <TrainingJobMonitorCard key={job.id} job={job} onOpen={setSelectedJob} />
        ))}
      </Stack>
      <TrainingJobDetailsDialog
        job={selectedJob}
        open={Boolean(selectedJob)}
        onClose={() => setSelectedJob(null)}
      />
    </>
  );
};

const VersionCards: React.FC<{ versions: ModelLabVersion[] }> = ({ versions }) => {
  if (versions.length === 0) {
    return (
      <EmptyOperationState
        title="No model versions yet"
        message="A completed training job will create the first version for this model."
      />
    );
  }

  return (
    <Stack spacing={1}>
      {[...versions].sort((a, b) => b.version_number - a.version_number).map((version) => (
        <Paper key={version.id} variant="outlined" sx={{ p: 1.25, bgcolor: 'background.paper' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography variant="subtitle2">Version v{version.version_number}</Typography>
                <Chip size="small" label={version.stage} color={modelStageColor(version.stage)} />
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                Dataset: {version.dataset_version_name || 'not recorded'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              {formatMetricSummary(version.metrics).map((metric) => (
                <Chip key={metric} size="small" variant="outlined" label={metric} />
              ))}
            </Stack>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

const DeploymentCards: React.FC<{ deployments: ModelLabDeployment[] }> = ({ deployments }) => {
  if (deployments.length === 0) {
    return (
      <EmptyOperationState
        title="No deployments yet"
        message="Deploy a trained version before running live inference from this model."
      />
    );
  }

  return (
    <Stack spacing={1}>
      {deployments.map((deployment) => (
        <Paper key={deployment.id} variant="outlined" sx={{ p: 1.25, bgcolor: 'background.paper' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography variant="subtitle2" noWrap>{deployment.name}</Typography>
                <Chip size="small" label={deployment.status} color={deploymentStatusColor(deployment.status)} />
                {deployment.is_active ? <Chip size="small" color="success" variant="outlined" label="active" /> : null}
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                Endpoint: {deployment.endpoint_slug || 'not assigned'} - Version: {deployment.model_version_name || `#${deployment.model_version}`}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

const formatAnalysisNumber = (value: unknown, suffix = ''): string => {
  if (value === null || value === undefined || value === '') return 'n/a';
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  const formatted = Math.abs(number) >= 1000
    ? number.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : number.toLocaleString(undefined, { maximumFractionDigits: 4 });
  return `${formatted}${suffix}`;
};

const readinessColor = (score: number): 'success' | 'warning' | 'error' => {
  if (score >= 80) return 'success';
  if (score >= 55) return 'warning';
  return 'error';
};

const MetricTile: React.FC<{ label: string; value: React.ReactNode; detail?: React.ReactNode; color?: string }> = ({
  label,
  value,
  detail,
  color = 'primary.main',
}) => (
  <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.paper', minHeight: 88 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" sx={{ color, lineHeight: 1.25 }}>{value}</Typography>
    {detail ? <Typography variant="caption" color="text.secondary" display="block" noWrap>{detail}</Typography> : null}
  </Paper>
);

const PercentBar: React.FC<{ value: number; color?: string; height?: number }> = ({ value, color = 'primary.main', height = 6 }) => (
  <Box sx={{ height, bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
    <Box sx={{ height: '100%', width: `${Math.max(0, Math.min(100, value))}%`, bgcolor: color }} />
  </Box>
);

const ChartPanel: React.FC<{ title: string; subtitle?: React.ReactNode; children: React.ReactNode; minHeight?: number }> = ({
  title,
  subtitle,
  children,
  minHeight = 260,
}) => (
  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper', minHeight }}>
    <Typography variant="subtitle2" gutterBottom>{title}</Typography>
    {subtitle ? (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {subtitle}
      </Typography>
    ) : null}
    {children}
  </Paper>
);

const EmptyChartState: React.FC<{ message: string }> = ({ message }) => (
  <Box sx={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'text.secondary' }}>
    <Typography variant="body2">{message}</Typography>
  </Box>
);

const histogramChartData = (column?: DataProfileColumn | null) => {
  const histogram = column?.histogram;
  if (!histogram?.counts?.length || !histogram.edges?.length) return [];
  return histogram.counts.map((count, index) => {
    const start = histogram.edges[index];
    const end = histogram.edges[index + 1];
    return {
      bucket: `${formatAnalysisNumber(start)} - ${formatAnalysisNumber(end)}`,
      count,
    };
  });
};

const valueCountChartData = (column?: DataProfileColumn | null) => {
  if (!column?.value_counts) return [];
  return Object.entries(column.value_counts).slice(0, 12).map(([name, count]) => ({
    name,
    count,
  }));
};

const seasonalityStrengthLabel = (strength: number | null | undefined) => {
  const value = Number(strength || 0);
  if (value >= 0.15) return 'strong';
  if (value >= 0.05) return 'moderate';
  if (value > 0) return 'weak';
  return 'none';
};

const seasonalityStrengthColor = (strength: number | null | undefined): 'success' | 'warning' | 'default' => {
  const label = seasonalityStrengthLabel(strength);
  if (label === 'strong') return 'success';
  if (label === 'moderate') return 'warning';
  return 'default';
};

const correlationColor = (value: number) => {
  const intensity = Math.min(0.9, Math.abs(value));
  if (value >= 0) return `rgba(25, 118, 210, ${0.12 + intensity * 0.75})`;
  return `rgba(245, 124, 0, ${0.12 + intensity * 0.75})`;
};

const AdvancedAnalysisReport: React.FC<{
  report: DatasetAnalysisReport;
  selectedFeatureSet?: ModelLabFeatureSet | null;
  isFetching?: boolean;
}> = ({ report, selectedFeatureSet = null, isFetching = false }) => {
  const readiness = report.readiness || {
    score: 0,
    grade: 'unknown',
    recommended_validation: 'unknown',
    blockers: [],
    cautions: [],
    recommended_actions: [],
  };
  const numericDiagnostics = report.numeric_diagnostics?.columns || [];
  const categoricalColumns = report.categorical_diagnostics?.categorical_columns || [];
  const textColumns = report.categorical_diagnostics?.text_columns || [];
  const schemaColumns = report.schema?.columns || [];
  const schemaCounts = Object.entries(report.schema?.semantic_type_counts || {});
  const warningMessages = [
    ...(report.warnings || []).map((warning) => ({ severity: warning.severity, message: warning.message, columns: warning.columns })),
    ...(report.time_series?.warnings || []).map((message) => ({ severity: 'warning' as const, message })),
  ];
  const maxMissing = Math.max(1, ...((report.quality?.columns || []).map((column) => column.missing_percent || 0)));
  const targetClassBalance = report.target_analysis?.class_balance || [];
  const seasonalityStrengths = report.seasonality?.strength_scores || [];
  const seasonalityPatterns = report.seasonality?.dominant_patterns || [];
  const seasonalitySummaries = report.seasonality?.summaries || [];
  const autocorrelation = report.seasonality?.autocorrelation || [];
  const groupProfiles = report.time_series?.group_profiles || [];
  const largestGaps = report.time_series?.largest_gaps || [];
  const intervalDistribution = report.time_series?.interval_distribution_seconds || [];
  const sessionDistribution = report.time_series?.session_distribution || [];
  const featureAnalysis = report.feature_analysis || null;
  const featureTransformFailed = featureAnalysis?.transform_status === 'failed';
  const selectedFeatureSetId = selectedFeatureSet?.id ?? null;
  const appliedFeatureSetId = featureAnalysis?.id ?? null;
  const featureReportMatchesSelection = !selectedFeatureSetId || appliedFeatureSetId === selectedFeatureSetId;
  const awaitingSelectedFeatureReport = Boolean(selectedFeatureSetId && !featureReportMatchesSelection);
  const targetMissingMessage = 'Target column was not found in this dataset version.';
  const visibleBlockers = awaitingSelectedFeatureReport
    ? readiness.blockers.filter((message) => !message.toLowerCase().includes('target column'))
    : readiness.blockers;
  const visibleWarnings = awaitingSelectedFeatureReport
    ? warningMessages.filter((warning) => warning.message !== targetMissingMessage)
    : warningMessages;
  const [checksExpanded, setChecksExpanded] = useState(false);
  const histogramColumns = useMemo(
    () => report.columns.filter((column) => column.histogram?.counts?.length),
    [report.columns],
  );
  const categoricalProfileColumns = useMemo(
    () => report.columns.filter((column) => column.value_counts && Object.keys(column.value_counts).length > 0),
    [report.columns],
  );
  const [selectedHistogramColumn, setSelectedHistogramColumn] = useState('');
  const [selectedCategoricalChartColumn, setSelectedCategoricalChartColumn] = useState('');
  useEffect(() => {
    if (!selectedHistogramColumn || !histogramColumns.some((column) => column.name === selectedHistogramColumn)) {
      setSelectedHistogramColumn(histogramColumns[0]?.name || '');
    }
  }, [histogramColumns, selectedHistogramColumn]);
  useEffect(() => {
    if (!selectedCategoricalChartColumn || !categoricalProfileColumns.some((column) => column.name === selectedCategoricalChartColumn)) {
      setSelectedCategoricalChartColumn(categoricalProfileColumns[0]?.name || '');
    }
  }, [categoricalProfileColumns, selectedCategoricalChartColumn]);
  const profileByName = useMemo(() => {
    const byName = new Map<string, DataProfileColumn>();
    report.columns.forEach((column) => byName.set(column.name, column));
    return byName;
  }, [report.columns]);
  const selectedHistogramProfile = selectedHistogramColumn ? profileByName.get(selectedHistogramColumn) : histogramColumns[0];
  const selectedCategoricalProfile = selectedCategoricalChartColumn ? profileByName.get(selectedCategoricalChartColumn) : categoricalProfileColumns[0];
  const selectedHistogramData = histogramChartData(selectedHistogramProfile);
  const selectedCategoricalData = valueCountChartData(selectedCategoricalProfile);
  const featureNameSet = useMemo(
    () => new Set([
      ...(featureAnalysis?.requested_feature_columns || []),
      ...(featureAnalysis?.custom_feature_columns || []),
      ...(featureAnalysis?.transform_result_columns || []),
      ...(featureAnalysis?.generated_columns || []),
    ]),
    [featureAnalysis],
  );
  useEffect(() => {
    if (!featureReportMatchesSelection || !featureNameSet.size) return;
    const preferredFeatureColumn = histogramColumns.find((column) => featureNameSet.has(column.name));
    if (preferredFeatureColumn && !featureNameSet.has(selectedHistogramColumn)) {
      setSelectedHistogramColumn(preferredFeatureColumn.name);
    }
  }, [featureNameSet, featureReportMatchesSelection, histogramColumns, selectedHistogramColumn]);
  const chartDataNumericColumns = report.chart_data?.numeric_columns || [];
  const [scatterXColumn, setScatterXColumn] = useState('');
  const [scatterYColumn, setScatterYColumn] = useState('');
  useEffect(() => {
    if (!chartDataNumericColumns.length) return;
    const targetColumn = report.chart_data?.target_column || report.target_analysis?.target_column || '';
    const preferredX = chartDataNumericColumns.find((column) => column !== targetColumn && featureNameSet.has(column))
      || chartDataNumericColumns.find((column) => column !== targetColumn)
      || chartDataNumericColumns[0];
    const preferredY = targetColumn && chartDataNumericColumns.includes(targetColumn)
      ? targetColumn
      : chartDataNumericColumns.find((column) => column !== preferredX) || chartDataNumericColumns[0];
    if (!scatterXColumn || !chartDataNumericColumns.includes(scatterXColumn)) {
      setScatterXColumn(preferredX);
    }
    if (!scatterYColumn || !chartDataNumericColumns.includes(scatterYColumn) || scatterYColumn === preferredX) {
      setScatterYColumn(preferredY);
    }
  }, [chartDataNumericColumns, featureNameSet, report.chart_data?.target_column, report.target_analysis?.target_column, scatterXColumn, scatterYColumn]);
  const realScatterChartData = (report.chart_data?.scatter_rows || [])
    .map((row) => ({
      x: Number(row[scatterXColumn]),
      y: Number(row[scatterYColumn]),
      timestamp: report.chart_data?.timestamp_column ? row[report.chart_data.timestamp_column] : undefined,
    }))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
  const featureDiagnostics = useMemo(() => {
    const numericByName = new Map(numericDiagnostics.map((column) => [column.name, column]));
    const fromFeatureReport = (featureAnalysis?.features || []).map((feature) => ({
      name: feature.name,
      exists: feature.exists,
      role: feature.role,
      missing_percent: Number(feature.missing_percent || 0),
      unique_count: Number(feature.unique_count || 0),
      target_correlation: feature.target_correlation ?? null,
      semantic_type: feature.semantic_type || (numericByName.has(feature.name) ? 'numeric' : 'feature'),
    }));
    if (fromFeatureReport.length) return fromFeatureReport;
    return numericDiagnostics.slice(0, 12).map((column) => ({
      name: column.name,
      exists: true,
      role: featureNameSet.has(column.name) ? 'feature' : 'raw',
      missing_percent: Number(column.missing_percent || 0),
      unique_count: Number(column.count || 0),
      target_correlation: null,
      semantic_type: 'numeric',
    }));
  }, [featureAnalysis, featureNameSet, numericDiagnostics]);
  const featureMissingChartData = featureDiagnostics.slice(0, 16).map((feature) => ({
    name: feature.name,
    missing: Number(feature.missing_percent || 0),
    role: feature.role,
  }));
  const featureTargetScatterData = featureDiagnostics
    .filter((feature) => feature.exists && feature.target_correlation != null)
    .map((feature) => ({
      name: feature.name,
      x: Number(feature.unique_count || 0),
      y: Number(feature.target_correlation || 0),
      missing: Number(feature.missing_percent || 0),
    }));
  const topTargetCorrelations = (report.feature_screening?.target_correlations || []).slice(0, 12).map((item) => ({
    column: item.column,
    correlation: Number(item.correlation || 0),
    absolute: Math.abs(Number(item.correlation || 0)),
  }));
  const highCorrelationPairs = (report.high_correlations || report.feature_screening.high_correlation_pairs || []).slice(0, 14).map((item) => ({
    pair: `${item.left} / ${item.right}`,
    correlation: Number(item.correlation || 0),
    absolute: Math.abs(Number(item.correlation || 0)),
  }));
  const correlationColumns = (report.correlation?.columns || []).slice(0, 10);
  const correlationMatrix = report.correlation?.matrix || [];
  const strongestSeasonality = [...seasonalityStrengths]
    .filter((item) => item.strength != null)
    .sort((left, right) => Number(right.strength || 0) - Number(left.strength || 0))[0];
  const seasonalitySummaryData = seasonalityStrengths.map((item) => ({
    part: item.part.replaceAll('_', ' '),
    strength: Number(item.strength || 0) * 100,
    label: seasonalityStrengthLabel(item.strength),
  }));
  const primarySeasonalitySummary = seasonalitySummaries[0] || null;
  const primarySeasonalityBuckets = (primarySeasonalitySummary?.buckets || []).map((bucket) => ({
    bucket: String(bucket.bucket),
    mean: Number(bucket.mean || 0),
    count: Number(bucket.count || 0),
  }));
  const autocorrelationChartData = autocorrelation.map((item) => ({
    lag: String(item.lag),
    correlation: Number(item.correlation || 0),
  }));
  const checkCount = Number(awaitingSelectedFeatureReport)
    + visibleBlockers.length
    + readiness.cautions.length
    + readiness.recommended_actions.length
    + visibleWarnings.length;
  const severeCheckCount = visibleBlockers.length + visibleWarnings.filter((warning) => warning.severity === 'error').length;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2} alignItems={{ xs: 'stretch', xl: 'center' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="subtitle2">Modeling readiness</Typography>
              <Chip size="small" color={readinessColor(readiness.score)} label={readiness.grade.replaceAll('_', ' ')} />
              <Chip size="small" variant="outlined" label={`Validation: ${readiness.recommended_validation.replaceAll('_', ' ')}`} />
            </Stack>
            <Box sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h5" sx={{ width: 72 }}>{readiness.score}</Typography>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress variant="determinate" value={readiness.score} color={readinessColor(readiness.score)} sx={{ height: 8, borderRadius: 1 }} />
                </Box>
              </Stack>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {schemaCounts.map(([type, count]) => (
              <Chip key={type} size="small" variant="outlined" label={`${type}: ${count}`} />
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', xl: 'repeat(6, minmax(0, 1fr))' }, gap: 1 }}>
        <MetricTile label="Rows" value={report.row_count.toLocaleString()} detail={`${report.column_count} columns`} />
        <MetricTile label="Missing cells" value={`${report.quality.missing_cell_percent.toFixed(2)}%`} detail={`${report.quality.missing_cell_count.toLocaleString()} cells`} color={report.quality.missing_cell_percent > 5 ? 'warning.main' : 'success.main'} />
        <MetricTile label="Duplicates" value={report.quality.duplicate_row_count.toLocaleString()} detail={`${formatAnalysisNumber(report.quality.duplicate_row_percent, '%')} rows`} color={report.quality.duplicate_row_count ? 'warning.main' : 'success.main'} />
        <MetricTile label="Timestamp gaps" value={formatAnalysisNumber(report.time_series.gap_count || 0)} detail={report.time_series.estimated_interval_seconds ? `${report.time_series.estimated_interval_seconds}s interval` : 'interval unknown'} color={report.time_series.gap_count ? 'warning.main' : 'success.main'} />
        <MetricTile label="Leakage flags" value={report.feature_screening.leakage_candidates.length} detail={`${report.feature_screening.target_correlations.length} target correlations`} color={report.feature_screening.leakage_candidates.length ? 'error.main' : 'success.main'} />
        <MetricTile label="Outlier columns" value={report.numeric_diagnostics.outlier_leaders.length} detail={`${report.numeric_diagnostics.columns.length} numeric`} color={report.numeric_diagnostics.outlier_leaders.length ? 'warning.main' : 'success.main'} />
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2} alignItems={{ xs: 'stretch', xl: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="subtitle2">Feature-set analysis</Typography>
              {featureAnalysis && featureReportMatchesSelection ? (
                <>
                  <Chip size="small" color="primary" variant="outlined" label={featureAnalysis.name || `Feature set #${featureAnalysis.id}`} />
                  {featureTransformFailed ? (
                    <Chip size="small" color="error" variant="outlined" label="Transform failed" />
                  ) : (
                    <>
                      <Chip size="small" variant="outlined" label={`${featureAnalysis.feature_count} usable features`} />
                      <Chip size="small" color={featureAnalysis.ready_row_percent < 80 ? 'warning' : 'success'} variant="outlined" label={`${featureAnalysis.ready_row_percent.toFixed(2)}% training-ready rows`} />
                    </>
                  )}
                </>
              ) : selectedFeatureSet ? (
                <>
                  <Chip size="small" color="warning" variant="outlined" label={`Feature set selected: ${selectedFeatureSet.name}`} />
                  {isFetching ? <Chip size="small" color="info" variant="outlined" label="Applying feature set" /> : null}
                </>
              ) : (
                <Chip size="small" variant="outlined" label="Raw dataset only" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
              {featureAnalysis && featureReportMatchesSelection
                ? featureTransformFailed
                  ? `Selected feature recipe/transforms could not be applied. Raw ${featureAnalysis.raw_column_count} columns are shown so schema and compatibility issues remain visible.`
                  : `Analysis is running after the selected feature recipe/transforms. Raw ${featureAnalysis.raw_column_count} columns became ${featureAnalysis.output_column_count} columns.`
                : selectedFeatureSet
                  ? 'Waiting for the selected feature-set analysis. Target warnings from the raw dataset are hidden until the feature recipe/transforms are applied.'
                  : 'Select a feature set above to analyze the engineered features that the model will train on.'}
            </Typography>
          </Box>
          {featureAnalysis && featureReportMatchesSelection ? (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ maxWidth: { xl: '55%' } }}>
              {featureAnalysis.pipeline ? <Chip size="small" label={`Pipeline ${featureAnalysis.pipeline}`} /> : null}
              <Chip size="small" label={`Transforms ${featureAnalysis.transform_count}`} />
              <Chip size="small" color={(featureAnalysis.custom_feature_columns || []).length ? 'primary' : 'default'} variant="outlined" label={`Custom ${(featureAnalysis.custom_feature_columns || []).length}`} />
              <Chip size="small" label={`Generated ${featureAnalysis.generated_columns.length}`} />
              <Chip size="small" color={featureAnalysis.missing_feature_columns.length ? 'error' : 'success'} variant="outlined" label={`Missing ${featureAnalysis.missing_feature_columns.length}`} />
              {featureAnalysis.target_column ? <Chip size="small" variant="outlined" label={`Target ${featureAnalysis.target_column}`} /> : null}
            </Stack>
          ) : null}
        </Stack>
        {featureAnalysis && featureReportMatchesSelection && featureTransformFailed ? (
          <Alert severity="error" variant="outlined" sx={{ mt: 1.5 }}>
            {featureAnalysis.transform_error || 'The selected feature set could not be applied to this dataset schema.'}
          </Alert>
        ) : null}
        {featureAnalysis && featureReportMatchesSelection ? (
          <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>Requested and generated features</Typography>
              <Stack spacing={0.75}>
                {featureAnalysis.features.slice(0, 12).map((feature) => (
                  <Box key={feature.name}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="caption" fontWeight={700} noWrap>{feature.name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {feature.exists
                          ? `${feature.semantic_type || 'feature'} - ${formatAnalysisNumber(feature.missing_percent, '%')} missing${feature.target_correlation != null ? ` - corr ${formatAnalysisNumber(feature.target_correlation)}` : ''}`
                          : feature.warning}
                      </Typography>
                    </Stack>
                    {feature.exists ? <PercentBar value={Number(feature.missing_percent || 0)} color={Number(feature.missing_percent || 0) > 10 ? 'warning.main' : 'success.main'} height={5} /> : null}
                  </Box>
                ))}
              </Stack>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>Feature transform lineage</Typography>
              {(featureAnalysis.custom_feature_columns || []).length > 0 ? (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Custom features</Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                    {(featureAnalysis.custom_feature_columns || []).map((column) => (
                      <Chip key={`custom-${column}`} size="small" color="primary" variant="outlined" label={column} />
                    ))}
                  </Stack>
                </>
              ) : null}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Generated columns</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {featureAnalysis.generated_columns.slice(0, 20).map((column) => (
                  <Chip
                    key={column}
                    size="small"
                    color={(featureAnalysis.custom_feature_columns || []).includes(column) ? 'primary' : 'default'}
                    variant="outlined"
                    label={column}
                  />
                ))}
                {featureAnalysis.generated_columns.length === 0 ? <Chip size="small" variant="outlined" label="No generated columns" /> : null}
              </Stack>
              {featureAnalysis.missing_feature_columns.length > 0 ? (
                <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
                  Missing requested features: {featureAnalysis.missing_feature_columns.join(', ')}
                </Alert>
              ) : null}
            </Box>
          </Box>
        ) : null}
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
        <ChartPanel
          title="Numeric distribution"
          subtitle="Histogram from the currently analyzed data. When a feature set is applied, engineered numeric features are included here."
        >
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <TextField
              select
              size="small"
              label="Column"
              value={selectedHistogramColumn}
              onChange={(event) => setSelectedHistogramColumn(event.target.value)}
              sx={{ minWidth: 220 }}
            >
              {histogramColumns.map((column) => (
                <MenuItem key={column.name} value={column.name}>{column.name}</MenuItem>
              ))}
            </TextField>
            {selectedHistogramProfile ? (
              <Chip size="small" variant="outlined" label={`${selectedHistogramProfile.null_percent?.toFixed(2) || '0.00'}% null`} sx={{ alignSelf: 'center' }} />
            ) : null}
          </Stack>
          {selectedHistogramData.length ? (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={selectedHistogramData} margin={{ top: 8, right: 16, left: 0, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: '#b8c0cc' }} angle={-35} textAnchor="end" height={58} interval={0} />
                <YAxis tick={{ fontSize: 10, fill: '#b8c0cc' }} width={46} />
                <RechartsTooltip contentStyle={{ background: '#18191d', border: '1px solid #333842' }} />
                <Bar dataKey="count" fill="#f59f00" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChartState message="No numeric histogram is available for this analysis." />
          )}
        </ChartPanel>

        <ChartPanel
          title="Categorical and target distribution"
          subtitle="Top values from categorical, tag, text, or class-label columns."
        >
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <TextField
              select
              size="small"
              label="Column"
              value={selectedCategoricalChartColumn}
              onChange={(event) => setSelectedCategoricalChartColumn(event.target.value)}
              sx={{ minWidth: 220 }}
            >
              {categoricalProfileColumns.map((column) => (
                <MenuItem key={column.name} value={column.name}>{column.name}</MenuItem>
              ))}
            </TextField>
            {report.target_analysis?.exists && report.target_analysis.target_column ? (
              <Chip size="small" color="primary" variant="outlined" label={`target ${report.target_analysis.target_column}`} sx={{ alignSelf: 'center' }} />
            ) : null}
          </Stack>
          {selectedCategoricalData.length ? (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={selectedCategoricalData} layout="vertical" margin={{ top: 8, right: 20, left: 48, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#b8c0cc' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#b8c0cc' }} width={90} />
                <RechartsTooltip contentStyle={{ background: '#18191d', border: '1px solid #333842' }} />
                <Bar dataKey="count" fill="#7c4dff" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : targetClassBalance.length ? (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={targetClassBalance.slice(0, 12)} layout="vertical" margin={{ top: 8, right: 20, left: 48, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#b8c0cc' }} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 10, fill: '#b8c0cc' }} width={90} />
                <RechartsTooltip contentStyle={{ background: '#18191d', border: '1px solid #333842' }} />
                <Bar dataKey="count" fill="#7c4dff" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChartState message="No categorical distribution is available. Select a target or analyze a dataset with categorical/news/tag columns." />
          )}
        </ChartPanel>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
        <ChartPanel
          title="Feature health"
          subtitle="Engineered features from the selected feature set, including missingness and target relationship when available."
        >
          {featureDiagnostics.length ? (
            <Stack spacing={1.25}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={featureMissingChartData} margin={{ top: 8, right: 16, left: 0, bottom: 52 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#b8c0cc' }} angle={-35} textAnchor="end" height={62} interval={0} />
                  <YAxis tick={{ fontSize: 10, fill: '#b8c0cc' }} width={46} />
                  <RechartsTooltip contentStyle={{ background: '#18191d', border: '1px solid #333842' }} />
                  <Bar dataKey="missing" name="missing %" fill="#00bcd4" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {featureDiagnostics.slice(0, 18).map((feature) => (
                  <Tooltip key={feature.name} title={`${feature.role} - ${feature.exists ? 'available' : 'missing'}${feature.target_correlation != null ? ` - target corr ${formatAnalysisNumber(feature.target_correlation)}` : ''}`}>
                    <Chip
                      size="small"
                      color={!feature.exists ? 'error' : feature.target_correlation != null && Math.abs(Number(feature.target_correlation)) > 0.8 ? 'warning' : 'default'}
                      variant="outlined"
                      label={feature.name}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Stack>
          ) : (
            <EmptyChartState message="No feature-set diagnostics are available yet. Select a feature set and run analysis." />
          )}
        </ChartPanel>

        <ChartPanel
          title="Relationships and correlations"
          subtitle="Use this to spot redundant features and target leakage candidates before training."
        >
          {realScatterChartData.length ? (
            <Stack spacing={1}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  select
                  size="small"
                  label="X axis"
                  value={scatterXColumn}
                  onChange={(event) => setScatterXColumn(event.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  {chartDataNumericColumns.map((column) => (
                    <MenuItem key={`x-${column}`} value={column}>{column}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Y axis"
                  value={scatterYColumn}
                  onChange={(event) => setScatterYColumn(event.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  {chartDataNumericColumns.map((column) => (
                    <MenuItem key={`y-${column}`} value={column}>{column}</MenuItem>
                  ))}
                </TextField>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${realScatterChartData.length.toLocaleString()} sampled rows`}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
                />
              </Stack>
              <ResponsiveContainer width="100%" height={250}>
                <ScatterChart margin={{ top: 12, right: 20, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis type="number" dataKey="x" name={scatterXColumn} tick={{ fontSize: 10, fill: '#b8c0cc' }} />
                  <YAxis type="number" dataKey="y" name={scatterYColumn} tick={{ fontSize: 10, fill: '#b8c0cc' }} />
                  <RechartsTooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    contentStyle={{ background: '#18191d', border: '1px solid #333842' }}
                    formatter={(value, name) => [formatAnalysisNumber(value), name === 'x' ? scatterXColumn : scatterYColumn]}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.timestamp ? `time ${payload[0].payload.timestamp}` : `${scatterXColumn} vs ${scatterYColumn}`}
                  />
                  <Scatter data={realScatterChartData} fill="#f59f00" />
                </ScatterChart>
              </ResponsiveContainer>
            </Stack>
          ) : featureTargetScatterData.length ? (
            <ResponsiveContainer width="100%" height={250}>
              <ScatterChart margin={{ top: 12, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis type="number" dataKey="x" name="unique values" tick={{ fontSize: 10, fill: '#b8c0cc' }} />
                <YAxis type="number" dataKey="y" name="target correlation" tick={{ fontSize: 10, fill: '#b8c0cc' }} domain={[-1, 1]} />
                <RechartsTooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  contentStyle={{ background: '#18191d', border: '1px solid #333842' }}
                  formatter={(value, name) => [formatAnalysisNumber(value), name]}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''}
                />
                <Scatter data={featureTargetScatterData} fill="#f59f00" />
              </ScatterChart>
            </ResponsiveContainer>
          ) : topTargetCorrelations.length ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={topTargetCorrelations} layout="vertical" margin={{ top: 8, right: 20, left: 54, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#b8c0cc' }} domain={[0, 1]} />
                <YAxis type="category" dataKey="column" tick={{ fontSize: 10, fill: '#b8c0cc' }} width={100} />
                <RechartsTooltip contentStyle={{ background: '#18191d', border: '1px solid #333842' }} />
                <Bar dataKey="absolute" name="abs target correlation" fill="#f59f00" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : highCorrelationPairs.length ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={highCorrelationPairs} layout="vertical" margin={{ top: 8, right: 20, left: 86, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#b8c0cc' }} domain={[0, 1]} />
                <YAxis type="category" dataKey="pair" tick={{ fontSize: 10, fill: '#b8c0cc' }} width={140} />
                <RechartsTooltip contentStyle={{ background: '#18191d', border: '1px solid #333842' }} />
                <Bar dataKey="absolute" name="abs correlation" fill="#f59f00" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChartState message="No correlation data is available for this selection." />
          )}
        </ChartPanel>
      </Box>

      {correlationColumns.length > 1 && (
        <ChartPanel title="Correlation map" subtitle="Blue is positive correlation, orange is negative correlation. Darker cells are stronger.">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `120px repeat(${correlationColumns.length}, minmax(34px, 1fr))`,
              gap: 0.5,
              alignItems: 'center',
              overflowX: 'auto',
            }}
          >
            <Box />
            {correlationColumns.map((column) => (
              <Typography key={`head-${column}`} variant="caption" noWrap sx={{ transform: 'rotate(-35deg)', transformOrigin: 'left bottom', height: 42 }}>
                {column}
              </Typography>
            ))}
            {correlationColumns.map((rowColumn, rowIndex) => (
              <React.Fragment key={`row-${rowColumn}`}>
                <Typography variant="caption" noWrap fontWeight={700}>{rowColumn}</Typography>
                {correlationColumns.map((column, columnIndex) => {
                  const row = correlationMatrix[rowIndex] || [];
                  const value = Number(row[columnIndex] || 0);
                  return (
                    <Tooltip key={`${rowColumn}-${column}`} title={`${rowColumn} / ${column}: ${formatAnalysisNumber(value)}`}>
                      <Box sx={{ height: 30, borderRadius: 0.5, bgcolor: correlationColor(value), border: '1px solid rgba(255,255,255,0.08)' }} />
                    </Tooltip>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
        </ChartPanel>
      )}

      {checkCount > 0 && (
        <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.paper' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography variant="subtitle2">Checks and actions</Typography>
                <Chip size="small" color={severeCheckCount ? 'error' : 'warning'} variant="outlined" label={`${checkCount} items`} />
                {severeCheckCount ? <Chip size="small" color="error" label={`${severeCheckCount} critical`} /> : null}
                {awaitingSelectedFeatureReport ? <Chip size="small" color="info" variant="outlined" label="feature report pending" /> : null}
              </Stack>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                Compact by default so warnings do not dominate the analysis view.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              endIcon={checksExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setChecksExpanded((expanded) => !expanded)}
              sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
            >
              {checksExpanded ? 'Hide' : 'Show'} details
            </Button>
          </Stack>
          <Collapse in={checksExpanded} unmountOnExit>
            <Stack spacing={1} sx={{ mt: 1.25 }}>
              {awaitingSelectedFeatureReport ? (
                <Alert severity="info" variant="outlined">
                  Feature-set analysis is pending for {selectedFeatureSet?.name}. Target readiness checks will run after the backend returns the transformed dataset.
                </Alert>
              ) : null}
              {visibleBlockers.map((message) => <Alert key={`blocker-${message}`} severity="error" variant="outlined">{message}</Alert>)}
              {readiness.cautions.map((message) => <Alert key={`caution-${message}`} severity="warning" variant="outlined">{message}</Alert>)}
              {readiness.recommended_actions.map((message) => <Alert key={`action-${message}`} severity="info" variant="outlined">{message}</Alert>)}
              {visibleWarnings.slice(0, 12).map((warning, index) => (
                <Alert key={`${warning.message}-${index}`} severity={warning.severity === 'error' ? 'error' : warning.severity === 'warning' ? 'warning' : 'info'} variant="outlined">
                  {warning.message}{warning.columns?.length ? ` (${warning.columns.slice(0, 6).join(', ')})` : ''}
                </Alert>
              ))}
            </Stack>
          </Collapse>
        </Paper>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.1fr 0.9fr' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2" gutterBottom>Schema intelligence</Typography>
          <Stack spacing={1}>
            {schemaColumns.slice(0, 14).map((column) => (
              <Box key={column.name} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr 0.8fr 1fr' }, gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" noWrap fontWeight={700}>{column.name}</Typography>
                <Chip size="small" variant="outlined" label={column.semantic_type} />
                <Typography variant="caption" color="text.secondary">{column.dtype}{column.numeric_like ? ' / numeric-like' : ''}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {column.unique_count.toLocaleString()} unique - {column.missing_percent.toFixed(2)}% missing
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2" gutterBottom>Feature screening</Typography>
          <Stack spacing={1}>
            {report.feature_screening.leakage_candidates.length > 0 ? (
              report.feature_screening.leakage_candidates.slice(0, 6).map((item) => (
                <Alert key={`${item.column}-${item.reason}`} severity="error" variant="outlined">
                  <strong>{item.column}</strong>: {item.reason}
                </Alert>
              ))
            ) : (
              <Alert severity="success" variant="outlined">No high-confidence leakage flags from column names or target correlation.</Alert>
            )}
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {report.feature_screening.drop_candidates.slice(0, 12).map((column) => (
                <Chip key={column} size="small" color="warning" variant="outlined" label={`review ${column}`} />
              ))}
            </Stack>
            {report.feature_screening.split_risks.map((risk) => (
              <Typography key={risk} variant="caption" color="text.secondary" display="block">- {risk}</Typography>
            ))}
          </Stack>
        </Paper>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2" gutterBottom>Numeric diagnostics</Typography>
          <Stack spacing={1.25}>
            {numericDiagnostics.length === 0 && <Typography variant="body2" color="text.secondary">No numeric columns detected.</Typography>}
            {numericDiagnostics.slice(0, 8).map((column) => (
              <Box key={column.name}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography variant="caption" fontWeight={700} noWrap>{column.name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    mean {formatAnalysisNumber(column.mean)} / std {formatAnalysisNumber(column.std)} / skew {formatAnalysisNumber(column.skew)}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                  <Chip size="small" variant="outlined" label={`p05 ${formatAnalysisNumber(column.p05)}`} />
                  <Chip size="small" variant="outlined" label={`median ${formatAnalysisNumber(column.median)}`} />
                  <Chip size="small" variant="outlined" label={`p95 ${formatAnalysisNumber(column.p95)}`} />
                  <Chip size="small" color={column.iqr_outlier_percent > 2 ? 'warning' : 'default'} variant="outlined" label={`outliers ${column.iqr_outlier_percent.toFixed(2)}%`} />
                  {column.drift?.std_shift != null ? <Chip size="small" color={Math.abs(column.drift.std_shift) > 1 ? 'warning' : 'default'} variant="outlined" label={`drift ${formatAnalysisNumber(column.drift.std_shift)} std`} /> : null}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2" gutterBottom>Missingness and data quality</Typography>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip size="small" label={`Constant ${report.quality.constant_columns.length}`} />
              <Chip size="small" label={`All null ${report.quality.all_null_columns.length}`} />
              <Chip size="small" label={`High missing ${(report.quality.high_missing_columns || []).length}`} />
              <Chip size="small" label={`High-cardinality ${report.quality.high_cardinality_columns.length}`} />
              <Chip size="small" label={`Infinite ${report.quality.infinite_cell_count || 0}`} />
            </Stack>
            {(report.quality.columns || []).slice(0, 14).map((column) => (
              <Box key={column.name}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography variant="caption" noWrap>{column.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {column.missing_percent.toFixed(2)}% missing - {column.unique_count.toLocaleString()} unique
                  </Typography>
                </Stack>
                <PercentBar value={(column.missing_percent / maxMissing) * 100} color={column.missing_percent ? 'warning.main' : 'success.main'} />
              </Box>
            ))}
          </Stack>
        </Paper>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2" gutterBottom>Categorical and tag diagnostics</Typography>
          <Stack spacing={1.25}>
            {categoricalColumns.length === 0 && <Typography variant="body2" color="text.secondary">No categorical columns detected.</Typography>}
            {categoricalColumns.slice(0, 6).map((column) => (
              <Box key={column.name}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" fontWeight={700}>{column.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {column.unique_count.toLocaleString()} unique - entropy {formatAnalysisNumber(column.normalized_entropy)}
                  </Typography>
                </Stack>
                {column.top_values.slice(0, 4).map((item) => (
                  <Box key={`${column.name}-${item.value}`}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary" noWrap>{item.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.percent.toFixed(2)}%</Typography>
                    </Stack>
                    <PercentBar value={item.percent} color="secondary.main" height={5} />
                  </Box>
                ))}
              </Box>
            ))}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2" gutterBottom>Text/news diagnostics</Typography>
          <Stack spacing={1.25}>
            {textColumns.length === 0 && <Typography variant="body2" color="text.secondary">No text-heavy columns detected.</Typography>}
            {textColumns.slice(0, 5).map((column) => (
              <Box key={column.name}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" fontWeight={700}>{column.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    avg {formatAnalysisNumber(column.avg_token_count)} tokens - {column.unique_count.toLocaleString()} unique
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {column.top_terms.slice(0, 10).map((term) => (
                    <Chip key={`${column.name}-${term.term}`} size="small" variant="outlined" label={`${term.term} ${term.count}`} />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Paper>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2" gutterBottom>Time-series coverage</Typography>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip size="small" label={`Timestamp ${report.time_series.timestamp_column || 'not detected'}`} />
              <Chip size="small" label={`Symbol ${report.time_series.symbol_column || 'none'}`} />
              <Chip size="small" label={`Timeframe ${report.time_series.timeframe_column || 'none'}`} />
              <Chip size="small" label={`Valid timestamps ${formatAnalysisNumber(report.time_series.valid_timestamp_percent, '%')}`} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Range: {report.time_series.start || 'unknown'} to {report.time_series.end || 'unknown'}
            </Typography>
            {groupProfiles.slice(0, 8).map((profile, index) => {
              const group = profile.group && typeof profile.group === 'object'
                ? Object.entries(profile.group as Record<string, unknown>).map(([key, value]) => `${key}: ${value}`).join(' / ')
                : `group ${index + 1}`;
              return (
                <Box key={`${group}-${index}`}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="caption" noWrap>{group}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatAnalysisNumber(profile.rows)} rows - {formatAnalysisNumber(profile.coverage_percent, '%')} coverage
                    </Typography>
                  </Stack>
                  <PercentBar value={Number(profile.coverage_percent || 0)} color={Number(profile.coverage_percent || 0) < 95 ? 'warning.main' : 'success.main'} />
                </Box>
              );
            })}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2" gutterBottom>Intervals, sessions, and gaps</Typography>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {intervalDistribution.slice(0, 6).map((item) => (
                <Chip key={item.seconds} size="small" variant="outlined" label={`${item.seconds}s x ${item.count}`} />
              ))}
            </Stack>
            {sessionDistribution.slice(0, 5).map((item) => (
              <Box key={item.session}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption">{item.session.replaceAll('_', ' ')}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.rows.toLocaleString()} rows - {item.percent.toFixed(2)}%</Typography>
                </Stack>
                <PercentBar value={item.percent} color="info.main" height={5} />
              </Box>
            ))}
            {largestGaps.slice(0, 5).map((gap, index) => (
              <Alert key={`${gap.from}-${gap.to}-${index}`} severity="warning" variant="outlined">
                Gap {formatAnalysisNumber(gap.gap_seconds)}s from {String(gap.from)} to {String(gap.to)}
              </Alert>
            ))}
          </Stack>
        </Paper>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2" gutterBottom>Target diagnostics</Typography>
          {awaitingSelectedFeatureReport ? (
            <Alert severity="info" variant="outlined">
              Waiting for transformed columns from {selectedFeatureSet?.name}; raw-dataset target errors are ignored for this selection.
            </Alert>
          ) : !report.target_analysis ? (
            <Alert severity="info" variant="outlined">Choose a target column to run class balance, baseline, and leakage checks.</Alert>
          ) : !report.target_analysis.exists ? (
            <Alert severity="error" variant="outlined">{report.target_analysis.warning}</Alert>
          ) : targetClassBalance.length ? (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip size="small" variant="outlined" label={`baseline ${formatAnalysisNumber(report.target_analysis.baseline_accuracy, '%')}`} />
                <Chip size="small" variant="outlined" label={`imbalance ${formatAnalysisNumber(report.target_analysis.imbalance_ratio)}`} />
                <Chip size="small" variant="outlined" label={`entropy ${formatAnalysisNumber(report.target_analysis.entropy)}`} />
              </Stack>
              {targetClassBalance.slice(0, 12).map((item) => (
                <Box key={item.label}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="caption">{item.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.count.toLocaleString()} rows - {item.percent.toFixed(2)}%</Typography>
                  </Stack>
                  <PercentBar value={item.percent} color="primary.main" />
                </Box>
              ))}
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {Object.entries(report.target_analysis.stats || {}).map(([key, value]) => (
                <Chip key={key} size="small" variant="outlined" label={`${key}: ${formatAnalysisNumber(value)}`} />
              ))}
              {Object.entries(report.target_analysis.distribution_shape || {}).map(([key, value]) => (
                <Chip key={key} size="small" variant="outlined" label={`${key}: ${formatAnalysisNumber(value)}`} />
              ))}
            </Stack>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2" gutterBottom>Seasonality and temporal signal</Typography>
          <Stack spacing={1.25}>
            <Alert severity="info" variant="outlined">
              Seasonality means the selected metric behaves differently by calendar bucket, such as hour of day, weekday, or month. Strength is the share of variation explained by that bucket: under 5% is weak, 5-15% is moderate, above 15% is strong.
            </Alert>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip size="small" label={`Metric: ${report.seasonality.metric_column || 'not selected'}`} />
              <Chip size="small" variant="outlined" label={`Span: ${formatAnalysisNumber(report.seasonality.span_days)} days`} />
              {strongestSeasonality ? (
                <Chip
                  size="small"
                  color={seasonalityStrengthColor(strongestSeasonality.strength)}
                  variant="outlined"
                  label={`Strongest: ${strongestSeasonality.part.replaceAll('_', ' ')} ${formatAnalysisNumber(Number(strongestSeasonality.strength || 0) * 100, '%')}`}
                />
              ) : null}
              {report.seasonality.metric_source ? <Chip size="small" variant="outlined" label={report.seasonality.metric_source.replaceAll('_', ' ')} /> : null}
            </Stack>
            {seasonalityPatterns.length > 0 ? (
              <Stack spacing={0.75}>
                {seasonalityPatterns.slice(0, 3).map((pattern) => (
                  <Alert key={`${pattern.part}-${pattern.metric}`} severity={pattern.label === 'strong' ? 'success' : pattern.label === 'moderate' ? 'warning' : 'info'} variant="outlined">
                    <strong>{pattern.part.replaceAll('_', ' ')}: {pattern.label}</strong> - {pattern.explanation}
                  </Alert>
                ))}
              </Stack>
            ) : (
              <Alert severity="info" variant="outlined">No clear repeating calendar pattern was detected for the selected metric.</Alert>
            )}
            {seasonalitySummaryData.length ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={seasonalitySummaryData} margin={{ top: 8, right: 16, left: 0, bottom: 28 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="part" tick={{ fontSize: 10, fill: '#b8c0cc' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#b8c0cc' }} width={44} label={{ value: 'strength %', angle: -90, position: 'insideLeft' }} />
                  <RechartsTooltip contentStyle={{ background: '#18191d', border: '1px solid #333842' }} />
                  <Bar dataKey="strength" fill="#f59f00" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : null}
            {primarySeasonalityBuckets.length ? (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Bucket means: {primarySeasonalitySummary?.part.replaceAll('_', ' ')} for {primarySeasonalitySummary?.metric}
                </Typography>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={primarySeasonalityBuckets} margin={{ top: 8, right: 16, left: 0, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: '#b8c0cc' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#b8c0cc' }} width={56} />
                    <RechartsTooltip contentStyle={{ background: '#18191d', border: '1px solid #333842' }} />
                    <Bar dataKey="mean" fill="#00bcd4" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : null}
            {autocorrelationChartData.length ? (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Autocorrelation by lag: positive values mean similar values repeat after that many rows.
                </Typography>
                <ResponsiveContainer width="100%" height={170}>
                  <LineChart data={autocorrelationChartData} margin={{ top: 8, right: 16, left: 0, bottom: 26 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="lag" tick={{ fontSize: 10, fill: '#b8c0cc' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#b8c0cc' }} width={44} domain={[-1, 1]} />
                    <RechartsTooltip contentStyle={{ background: '#18191d', border: '1px solid #333842' }} />
                    <Line type="monotone" dataKey="correlation" stroke="#f59f00" dot strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : null}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Feature encodings to test when signal is weak or moderate:
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {report.seasonality.suggested_features.map((feature) => (
                  <Tooltip key={feature.name} title={feature.description}>
                    <Chip size="small" color={feature.name.includes('hour') ? 'primary' : 'default'} variant="outlined" label={feature.name} />
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper' }}>
        <Typography variant="subtitle2" gutterBottom>Recommended modeling actions</Typography>
        <Stack spacing={1}>
          {report.feature_recommendations.map((recommendation) => (
            <Alert key={`${recommendation.category}-${recommendation.title}`} severity="info" variant="outlined">
              <strong>{recommendation.title}</strong>: {recommendation.detail}
            </Alert>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
};

const ModelLabBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { assetId: routeAssetId } = useParams<{ assetId?: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const shellRef = useRef<HTMLDivElement | null>(null);
  const draftAssetId = Number(searchParams.get('asset') || routeAssetId || 0) || null;
  const hydratedAssetSnapshotRef = useRef<string | null>(null);
  const [form, setForm] = useState<BuilderFormState>(initialForm);
  const [stack, setStack] = useState<ModelStackState>(initialStack);
  const [lifecycle, setLifecycle] = useState<LifecycleState>(initialLifecycle);
  const [savedAssetId, setSavedAssetId] = useState<number | null>(null);
  const [activeOperation, setActiveOperation] = useState<BuilderOperation | null>(null);
  const [trainingForm, setTrainingForm] = useState<BuilderTrainingFormState>(initialBuilderTrainingForm);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedCanvasNodeId, setSelectedCanvasNodeId] = useState<string | null>(null);
  const [chatPaneWidth, setChatPaneWidth] = useState(() => (
    typeof window === 'undefined'
      ? 380
      : getBalancedChatPaneWidth(window.innerWidth, false, INSPECTOR_PANE_MIN_WIDTH)
  ));
  const [inspectorPaneWidth, setInspectorPaneWidth] = useState(360);
  const [isInspectorOpen, setIsInspectorOpen] = useState(false);
  const [canvasView, setCanvasView] = useState<CanvasView>('model');
  const [workspaceTab, setWorkspaceTab] = useState<BuilderWorkspaceTab>('canvas');
  const [canvasZoom, setCanvasZoom] = useState(1);
  const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 });
  const [analysisDatasetVersionId, setAnalysisDatasetVersionId] = useState('');
  const [analysisDatasetSearch, setAnalysisDatasetSearch] = useState('');
  const [analysisDatasetPage, setAnalysisDatasetPage] = useState(1);
  const [analysisDatasetVersionOptions, setAnalysisDatasetVersionOptions] = useState<ModelLabDatasetVersion[]>([]);
  const [analysisFeatureSetId, setAnalysisFeatureSetId] = useState('');
  const [analysisTargetColumn, setAnalysisTargetColumn] = useState('');
  const [isCanvasFullscreen, setIsCanvasFullscreen] = useState(false);
  const [isCodeFullscreen, setIsCodeFullscreen] = useState(false);
  const [isCanvasPanning, setIsCanvasPanning] = useState(false);
  const [isSetupOpen, setIsSetupOpen] = useState(!draftAssetId);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const canvasDragRef = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);
  const hasUserResizedChatPaneRef = useRef(false);

  const builderAssetQuery = useQuery({
    queryKey: ['model-lab-builder-asset', draftAssetId],
    queryFn: () => modelLabAssetApi.get(draftAssetId!),
    enabled: draftAssetId != null,
  });

  const visualStackQuery = useQuery({
    queryKey: ['model-lab-builder-visual-stack', draftAssetId],
    queryFn: () => modelLabAssetApi.getVisualStack(draftAssetId!),
    enabled: draftAssetId != null,
  });

  const currentModelAsset = builderAssetQuery.data;
  const operationAssetId = currentModelAsset?.id || draftAssetId || savedAssetId;
  const shouldLoadOperationJobs = ['jobs', 'review', 'testing', 'inference'].includes(activeOperation || '');
  const shouldLoadOperationVersions = ['review', 'versions', 'testing', 'inference'].includes(activeOperation || '');
  const shouldLoadOperationDeployments = ['testing', 'inference'].includes(activeOperation || '');
  const builderJobsQueryKey = useMemo(
    () => ['model-lab-builder-jobs', operationAssetId] as const,
    [operationAssetId],
  );
  const builderJobsSocketEnabled = Boolean(operationAssetId && activeOperation === 'jobs');
  useModelLabTrainingJobsSocket({
    modelAssetId: operationAssetId,
    enabled: builderJobsSocketEnabled,
    queryKey: builderJobsQueryKey,
    pageSize: 20,
  });
  const handleModelLabEvent = useCallback((event: { type: string }) => {
    if (event.type === 'deployment.updated') {
      queryClient.invalidateQueries({ queryKey: ['model-lab-builder-deployments', operationAssetId] });
    }
    if (event.type === 'prediction_test.updated') {
      queryClient.invalidateQueries({ queryKey: ['model-lab-prediction-test-run'] });
    }
    if (event.type === 'training_job.updated') {
      queryClient.invalidateQueries({ queryKey: ['model-lab-builder-versions', operationAssetId] });
    }
  }, [operationAssetId, queryClient]);
  const refreshModelLabSocketState = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['model-lab-builder-deployments', operationAssetId] });
    queryClient.invalidateQueries({ queryKey: ['model-lab-builder-versions', operationAssetId] });
    queryClient.invalidateQueries({ queryKey: ['model-lab-prediction-test-run'] });
  }, [operationAssetId, queryClient]);
  useModelLabEventsSocket({
    scope: operationAssetId ? `asset_${operationAssetId}` : 'all',
    enabled: Boolean(operationAssetId && ['testing', 'inference', 'review', 'versions'].includes(activeOperation || '')),
    onEvent: handleModelLabEvent,
    onReconnect: refreshModelLabSocketState,
  });

  const builderJobsQuery = useQuery({
    queryKey: builderJobsQueryKey,
    queryFn: () => modelLabTrainingJobApi.listSummary({
      model_asset: operationAssetId!,
      page_size: 20,
      ordering: '-created_at',
    }),
    enabled: Boolean(operationAssetId && shouldLoadOperationJobs),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: !builderJobsSocketEnabled,
    staleTime: builderJobsSocketEnabled ? 300000 : 30000,
  });

  const builderVersionsQuery = useQuery({
    queryKey: ['model-lab-builder-versions', operationAssetId],
    queryFn: () => modelLabVersionApi.list({ model_asset: operationAssetId!, page_size: 50 }),
    enabled: Boolean(operationAssetId && shouldLoadOperationVersions),
    staleTime: 30000,
  });

  const builderDeploymentsQuery = useQuery({
    queryKey: ['model-lab-builder-deployments', operationAssetId],
    queryFn: () => modelLabDeploymentApi.list({ model_asset: operationAssetId!, page_size: 50 }),
    enabled: Boolean(operationAssetId && shouldLoadOperationDeployments),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    staleTime: 30000,
  });

  const builderDatasetVersionsQuery = useQuery({
    queryKey: ['model-lab-builder-dataset-versions'],
    queryFn: () => modelLabDatasetVersionApi.list({ page_size: 100 }),
    enabled: activeOperation === 'training',
  });

  const builderFeatureSetsQuery = useQuery({
    queryKey: ['model-lab-builder-feature-sets'],
    queryFn: () => modelLabFeatureSetApi.list({ page_size: 100 }),
    enabled: activeOperation === 'training',
  });

  const analysisDatasetVersionsQuery = useQuery({
    queryKey: ['model-lab-builder-analysis-dataset-versions', analysisDatasetSearch, analysisDatasetPage],
    queryFn: () => modelLabDatasetVersionApi.list({
      page: analysisDatasetPage,
      page_size: 15,
      search: analysisDatasetSearch || undefined,
      ordering: '-created_at',
      include_records: false,
      latest_only: true,
    }),
    enabled: workspaceTab === 'analysis',
  });

  const analysisDatasetHasMore = Boolean(analysisDatasetVersionsQuery.data?.next);
  const selectedAnalysisVersionId = Number(analysisDatasetVersionId) || null;
  const selectedAnalysisVersion = useMemo(() => (
    analysisDatasetVersionOptions.find((version) => version.id === selectedAnalysisVersionId) || null
  ), [analysisDatasetVersionOptions, selectedAnalysisVersionId]);

  const analysisFeatureSetsQuery = useQuery({
    queryKey: ['model-lab-builder-analysis-feature-sets', selectedAnalysisVersion?.dataset],
    queryFn: () => modelLabFeatureSetApi.list({
      dataset: selectedAnalysisVersion!.dataset,
      is_active: true,
      page_size: 100,
      ordering: '-updated_at',
    }),
    enabled: workspaceTab === 'analysis' && Boolean(selectedAnalysisVersion?.dataset),
  });
  const analysisFeatureSets = (analysisFeatureSetsQuery.data?.results || []) as ModelLabFeatureSet[];
  const selectedAnalysisFeatureSetId = Number(analysisFeatureSetId) || null;
  const selectedAnalysisFeatureSet = useMemo(() => (
    analysisFeatureSets.find((featureSet) => String(featureSet.id) === analysisFeatureSetId) || null
  ), [analysisFeatureSets, analysisFeatureSetId]);

  const datasetAnalysisQuery = useQuery<DatasetAnalysisReport | ModelLabDataProcessingQueued>({
    queryKey: ['model-lab-builder-dataset-analysis', selectedAnalysisVersionId, selectedAnalysisFeatureSetId, analysisTargetColumn],
    queryFn: () => modelLabDatasetVersionApi.analysis(
      selectedAnalysisVersionId!,
      {
        ...(analysisTargetColumn ? { target: analysisTargetColumn } : {}),
        ...(selectedAnalysisFeatureSetId ? { feature_set: selectedAnalysisFeatureSetId } : {}),
      },
    ),
    enabled: workspaceTab === 'analysis' && Boolean(selectedAnalysisVersionId),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const queuedAnalysisJob = isQueuedDataProcessing(datasetAnalysisQuery.data)
    ? datasetAnalysisQuery.data
    : null;
  const datasetAnalysisReport = datasetAnalysisQuery.data && !queuedAnalysisJob
    ? datasetAnalysisQuery.data as DatasetAnalysisReport
    : null;

  const analysisColumns = useMemo(() => {
    const columnSet = new Set<string>();
    if (datasetAnalysisReport?.columns?.length) {
      datasetAnalysisReport.columns.forEach((column) => columnSet.add(column.name));
    }
    const definition = selectedAnalysisFeatureSet?.definition || {};
    const definitionFeatureColumns = Array.isArray(definition.feature_columns)
      ? definition.feature_columns
      : Array.isArray(definition.features)
        ? definition.features
        : [];
    definitionFeatureColumns.forEach((column) => columnSet.add(String(column)));
    const definitionTarget = definition.target_column
      || definition.target_name
      || definition.target?.column
      || (typeof definition.target === 'string' ? definition.target : '');
    if (definitionTarget) columnSet.add(String(definitionTarget));
    if (columnSet.size > 0) return Array.from(columnSet);
    if (selectedAnalysisVersion?.columns?.length) return selectedAnalysisVersion.columns;
    const firstRow = selectedAnalysisVersion?.records?.[0];
    if (firstRow) return Object.keys(firstRow);
    const schemaColumns = selectedAnalysisVersion?.schema?.columns;
    return Array.isArray(schemaColumns) ? schemaColumns as string[] : [];
  }, [datasetAnalysisReport?.columns, selectedAnalysisFeatureSet, selectedAnalysisVersion]);

  useEffect(() => {
    if (!currentModelAsset) return;
    const runtimeSpec = currentModelAsset.runtime_spec || {};
    const snapshotKey = `${currentModelAsset.id}:${currentModelAsset.updated_at || JSON.stringify(runtimeSpec)}`;
    if (hydratedAssetSnapshotRef.current === snapshotKey) return;
    const authoring = runtimeSpec.authoring || {};
    const noCode = {
      ...(authoring.no_code || {}),
      ...(authoring.stack_builder || {}),
    };
    const pythonAuthoring = authoring.python || runtimeSpec.python || {};
    const lifecyclePlan = runtimeSpec.lifecycle || authoring.lifecycle || noCode.lifecycle_plan || {};
    const featureColumns = Array.isArray(noCode.feature_columns)
      ? noCode.feature_columns.join(', ')
      : Array.isArray(noCode.features)
        ? noCode.features.join(', ')
        : undefined;
    const targetName = noCode.target_name
      || noCode.target?.column
      || noCode.target?.name
      || (typeof noCode.target === 'string' ? noCode.target : undefined);
    const objectiveMetric = noCode.objective_metric
      || noCode.metric
      || (Array.isArray(noCode.metrics) ? noCode.metrics[0] : undefined);

    setForm((current) => ({
      ...current,
      name: currentModelAsset.name || current.name,
      description: currentModelAsset.description || current.description,
      definitionMode: getDefinitionModeFromRuntime(runtimeSpec, current.definitionMode),
      family: currentModelAsset.family || current.family,
      framework: currentModelAsset.framework || current.framework,
      problemType: currentModelAsset.problem_type || current.problemType,
      tags: Array.isArray(currentModelAsset.tags) ? currentModelAsset.tags.join(', ') : current.tags,
      executor: runtimeSpec.executor || lifecyclePlan.training?.compute_target || current.executor,
      builderGoal: noCode.builder_goal || current.builderGoal,
      targetName: targetName || current.targetName,
      featureColumns: featureColumns || current.featureColumns,
      objectiveMetric: objectiveMetric || current.objectiveMetric,
      validationStrategy: normalizeValidationStrategy(
        currentModelAsset.validation_strategy
          || runtimeSpec.validation_strategy
          || noCode.validation_strategy
          || lifecyclePlan.training?.split_strategy,
        normalizeValidationStrategy(current.validationStrategy),
      ),
      customEntrypoint: pythonAuthoring.entrypoint || current.customEntrypoint,
      customAllowedLibraries: Array.isArray(pythonAuthoring.allowed_libraries) ? pythonAuthoring.allowed_libraries.join(', ') : current.customAllowedLibraries,
      customCode: pythonAuthoring.code || current.customCode,
    }));
    setStack((current) => hydrateStackFromRuntimeSpec(
      runtimeSpec,
      current,
      currentModelAsset.problem_type || 'CLASSIFICATION',
    ));
    setLifecycle((current) => hydrateLifecycleFromPlan(lifecyclePlan, current));
    hydratedAssetSnapshotRef.current = snapshotKey;
  }, [currentModelAsset]);

  useEffect(() => {
    if (!analysisDatasetVersionsQuery.data?.results) return;
    setAnalysisDatasetVersionOptions((current) => {
      const base = analysisDatasetPage === 1 ? [] : current;
      const byId = new Map(base.map((version) => [version.id, version]));
      analysisDatasetVersionsQuery.data.results.forEach((version) => {
        byId.set(version.id, version);
      });
      return Array.from(byId.values());
    });
  }, [analysisDatasetPage, analysisDatasetVersionsQuery.data?.results]);

  useEffect(() => {
    if (analysisDatasetVersionId || !analysisDatasetVersionOptions.length) return;
    const preferred = analysisDatasetVersionOptions.find(
      (version) => String(version.id) === trainingForm.datasetVersionId,
    );
    setAnalysisDatasetVersionId(String((preferred || analysisDatasetVersionOptions[0]).id));
  }, [analysisDatasetVersionId, analysisDatasetVersionOptions, trainingForm.datasetVersionId]);

  useEffect(() => {
    setAnalysisFeatureSetId('');
    setAnalysisTargetColumn('');
  }, [analysisDatasetVersionId]);

  useEffect(() => {
    if (analysisFeatureSetId || !analysisFeatureSets.length) return;
    const preferred = analysisFeatureSets.find(
      (featureSet) => String(featureSet.id) === trainingForm.featureSetId,
    );
    setAnalysisFeatureSetId(String((preferred || analysisFeatureSets[0]).id));
  }, [analysisFeatureSetId, analysisFeatureSets, trainingForm.featureSetId]);

  useEffect(() => {
    if (analysisTargetColumn || !selectedAnalysisFeatureSet) return;
    const targetColumn = selectedAnalysisFeatureSet.definition?.target_column
      || selectedAnalysisFeatureSet.definition?.target_name
      || selectedAnalysisFeatureSet.definition?.target?.column
      || (typeof selectedAnalysisFeatureSet.definition?.target === 'string' ? selectedAnalysisFeatureSet.definition.target : '');
    if (targetColumn) setAnalysisTargetColumn(String(targetColumn));
  }, [analysisTargetColumn, selectedAnalysisFeatureSet]);

  useEffect(() => {
    setTrainingForm((current) => ({
      ...current,
      targetColumn: current.targetColumn || form.targetName || 'target',
      featureColumns: current.featureColumns || form.featureColumns,
      executor: current.executor || form.executor,
    }));
  }, [form.executor, form.featureColumns, form.targetName]);

  const handleAgentConversationCompleted = useCallback(async () => {
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const rootKey = Array.isArray(query.queryKey) ? query.queryKey[0] : null;
        return typeof rootKey === 'string' && rootKey.startsWith('model-lab');
      },
    });

    if (draftAssetId) {
      await queryClient.refetchQueries({
        queryKey: ['model-lab-builder-asset', draftAssetId],
        type: 'active',
      });
    }
  }, [draftAssetId, queryClient]);

  useEffect(() => {
    if (!isDesktop) return;

    const syncBalancedChatPane = () => {
      if (hasUserResizedChatPaneRef.current) return;
      const shellWidth = shellRef.current?.getBoundingClientRect().width || window.innerWidth;
      setChatPaneWidth(getBalancedChatPaneWidth(shellWidth, isInspectorOpen, inspectorPaneWidth));
    };

    syncBalancedChatPane();
    window.addEventListener('resize', syncBalancedChatPane);

    return () => window.removeEventListener('resize', syncBalancedChatPane);
  }, [inspectorPaneWidth, isDesktop, isInspectorOpen]);

  const setCanvasZoomClamped = useCallback((nextZoom: number | ((current: number) => number)) => {
    setCanvasZoom((current) => clamp(
      typeof nextZoom === 'function' ? nextZoom(current) : nextZoom,
      CANVAS_MIN_ZOOM,
      CANVAS_MAX_ZOOM,
    ));
  }, []);

  const resetCanvasViewport = useCallback(() => {
    setCanvasZoom(1);
    setCanvasPan({ x: 0, y: 0 });
  }, []);

  const switchCanvasView = useCallback((nextView: CanvasView) => {
    setCanvasView(nextView);
    resetCanvasViewport();
  }, [resetCanvasViewport]);

  const handleCanvasWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    if (event.cancelable) {
      event.preventDefault();
    }
    event.stopPropagation();
    const direction = event.deltaY < 0 ? 1 : -1;
    setCanvasZoomClamped((current) => current + direction * CANVAS_ZOOM_STEP);
  }, [setCanvasZoomClamped]);

  const handleCanvasMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (target.closest('[data-model-canvas-node="true"], button, input, textarea')) return;
    event.preventDefault();
    canvasDragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      panX: canvasPan.x,
      panY: canvasPan.y,
    };
    setIsCanvasPanning(true);
  }, [canvasPan]);

  const handleCanvasMouseMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!canvasDragRef.current) return;
    event.preventDefault();
    const drag = canvasDragRef.current;
    setCanvasPan({
      x: drag.panX + event.clientX - drag.startX,
      y: drag.panY + event.clientY - drag.startY,
    });
  }, []);

  const stopCanvasPan = useCallback(() => {
    canvasDragRef.current = null;
    setIsCanvasPanning(false);
  }, []);

  const startPaneResize = useCallback((pane: 'chat' | 'inspector') => (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const initialChatWidth = chatPaneWidth;
    const initialInspectorWidth = inspectorPaneWidth;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const shellWidth = shellRef.current?.getBoundingClientRect().width || window.innerWidth;
      const totalHandleWidth = isInspectorOpen ? BUILDER_HANDLE_TOTAL_WIDTH : 8;
      const reservedInspectorWidth = isInspectorOpen ? inspectorPaneWidth : 0;
      const maxChatWidth = Math.max(
        CHAT_PANE_MIN_WIDTH,
        Math.min(shellWidth * 0.8, shellWidth - reservedInspectorWidth - CANVAS_PANE_MIN_WIDTH - totalHandleWidth),
      );
      const maxInspectorWidth = Math.max(
        INSPECTOR_PANE_MIN_WIDTH,
        Math.min(shellWidth * 0.45, shellWidth - chatPaneWidth - CANVAS_PANE_MIN_WIDTH - BUILDER_HANDLE_TOTAL_WIDTH),
      );

      if (pane === 'chat') {
        hasUserResizedChatPaneRef.current = true;
        const nextWidth = initialChatWidth + moveEvent.clientX - startX;
        setChatPaneWidth(Math.min(Math.max(nextWidth, CHAT_PANE_MIN_WIDTH), maxChatWidth));
        return;
      }

      const nextWidth = initialInspectorWidth - (moveEvent.clientX - startX);
      setInspectorPaneWidth(Math.min(Math.max(nextWidth, INSPECTOR_PANE_MIN_WIDTH), maxInspectorWidth));
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [chatPaneWidth, inspectorPaneWidth, isInspectorOpen]);

  const managerAgentQuery = useQuery({
    queryKey: ['model-lab-builder-agent'],
    queryFn: () => agentApi.getModelLabBuilder(),
    retry: false,
  });

  const modelLabAgentsQuery = useQuery({
    queryKey: ['model-lab-builder-agent-options'],
    queryFn: () =>
      agentApi.list(1, 25, { is_active: true, agent_type: 'MODEL_LAB_BUILDER' }),
    retry: false,
  });

  const fallbackAgentQuery = useQuery({
    queryKey: ['model-lab-builder-agent-fallback'],
    queryFn: async () => {
      const response = await agentApi.list(1, 25, { is_active: true });
      const agents = response.results || [];
      return agents.find((agent: any) =>
        agent.agent_type === 'MODEL_LAB_BUILDER' ||
        agent.agent_type === 'MODEL_LAB_MANAGER'
      ) || null;
    },
    enabled: managerAgentQuery.isError,
    retry: false,
  });

  const modelLabAgentOptions = useMemo(() => {
    const options = (modelLabAgentsQuery.data?.results || []).filter((agent: any) =>
      agent.agent_type === 'MODEL_LAB_BUILDER'
    );
    const defaultAgent = managerAgentQuery.data || fallbackAgentQuery.data;
    const combined = defaultAgent && !options.some((agent: any) => agent.id === defaultAgent.id)
      ? [defaultAgent, ...options]
      : options;

    return combined.filter((agent: any, index: number, list: any[]) =>
      agent?.id && list.findIndex((item: any) => item.id === agent.id) === index
    );
  }, [fallbackAgentQuery.data, managerAgentQuery.data, modelLabAgentsQuery.data]);

  useEffect(() => {
    if (selectedAgentId) return;
    if (modelLabAgentOptions[0]?.id) {
      setSelectedAgentId(modelLabAgentOptions[0].id);
    }
  }, [modelLabAgentOptions, selectedAgentId]);

  const selectedAgentDetailQuery = useQuery({
    queryKey: ['model-lab-builder-selected-agent', selectedAgentId],
    queryFn: () => (selectedAgentId ? agentApi.get(selectedAgentId) : null),
    enabled:
      !!selectedAgentId &&
      !modelLabAgentOptions.some((agent: any) => agent.id === selectedAgentId),
    retry: false,
  });

  const managerAgent = selectedAgentDetailQuery.data
    || modelLabAgentOptions.find((agent: any) => agent.id === selectedAgentId)
    || managerAgentQuery.data
    || fallbackAgentQuery.data;
  const isManagerAgentLoading =
    managerAgentQuery.isLoading ||
    fallbackAgentQuery.isLoading ||
    modelLabAgentsQuery.isLoading ||
    selectedAgentDetailQuery.isLoading;

  const visibleAlgorithms = useMemo(() => {
    const task = taskForProblem(form.problemType);
    return ALGORITHMS.filter((algorithm) => algorithm.tasks.includes(task));
  }, [form.problemType]);

  const syncFormToAlgorithm = (algorithmValue: string) => {
    const algorithm = getAlgorithm(algorithmValue);
    setForm((current) => ({
      ...current,
      family: algorithm.family,
      framework: algorithm.framework,
    }));
  };

  const setEstimatorAlgorithm = (estimatorId: string, algorithmValue: string) => {
    const algorithm = getAlgorithm(algorithmValue);
    setStack((current) => {
      if (estimatorId === current.singleEstimator.id) {
        return {
          ...current,
          singleEstimator: {
            ...current.singleEstimator,
            algorithm: algorithmValue,
            params: { ...algorithm.defaults },
          },
        };
      }

      if (estimatorId === current.metaLearner.id) {
        return {
          ...current,
          metaLearner: {
            ...current.metaLearner,
            algorithm: algorithmValue,
            params: { ...algorithm.defaults },
          },
        };
      }

      return {
        ...current,
        baseEstimators: current.baseEstimators.map((estimator) =>
          estimator.id === estimatorId
            ? { ...estimator, algorithm: algorithmValue, params: { ...algorithm.defaults } }
            : estimator,
        ),
      };
    });
    syncFormToAlgorithm(algorithmValue);
  };

  const setEstimatorParam = (estimatorId: string, key: string, value: string) => {
    setStack((current) => {
      if (estimatorId === current.singleEstimator.id) {
        return {
          ...current,
          singleEstimator: {
            ...current.singleEstimator,
            params: { ...current.singleEstimator.params, [key]: value },
          },
        };
      }

      if (estimatorId === current.metaLearner.id) {
        return {
          ...current,
          metaLearner: {
            ...current.metaLearner,
            params: { ...current.metaLearner.params, [key]: value },
          },
        };
      }

      return {
        ...current,
        baseEstimators: current.baseEstimators.map((estimator) =>
          estimator.id === estimatorId
            ? { ...estimator, params: { ...estimator.params, [key]: value } }
            : estimator,
        ),
      };
    });
  };

  const setPreprocessingParam = (stepId: string, key: string, value: string) => {
    setStack((current) => ({
      ...current,
      preprocessing: current.preprocessing.map((step) =>
        step.id === stepId ? { ...step, params: { ...step.params, [key]: value } } : step,
      ),
    }));
  };

  const addPreprocessingStep = (stepType: string) => {
    const template = PREPROCESSING_STEPS.find((step) => step.value === stepType) || PREPROCESSING_STEPS[0];
    const step = {
      id: createBlockId(),
      type: template.value,
      label: template.label,
      params: { ...template.params },
    };
    setStack((current) => ({ ...current, preprocessing: [...current.preprocessing, step] }));
    setSelectedCanvasNodeId(null);
    setSelectedNodeId(step.id);
    setIsInspectorOpen(true);
  };

  const addBaseEstimator = () => {
    const algorithm = visibleAlgorithms[0] || ALGORITHMS[1];
    const estimator = {
      id: createBlockId(),
      algorithm: algorithm.value,
      params: { ...algorithm.defaults },
    };
    setStack((current) => ({ ...current, baseEstimators: [...current.baseEstimators, estimator] }));
    setSelectedCanvasNodeId(null);
    setSelectedNodeId(estimator.id);
    setIsInspectorOpen(true);
  };

  const updateHiddenLayers = (estimatorId: string, layerSizes: number[]) => {
    setStack((current) => {
      const hiddenLayerValue = layerSizes.join(',');
      if (estimatorId === current.singleEstimator.id) {
        return {
          ...current,
          singleEstimator: {
            ...current.singleEstimator,
            algorithm: 'mlp',
            params: {
              ...getAlgorithm('mlp').defaults,
              ...current.singleEstimator.params,
              hidden_layer_sizes: hiddenLayerValue,
            },
          },
        };
      }

      if (estimatorId === current.metaLearner.id) {
        return {
          ...current,
          metaLearner: {
            ...current.metaLearner,
            algorithm: 'mlp',
            params: {
              ...getAlgorithm('mlp').defaults,
              ...current.metaLearner.params,
              hidden_layer_sizes: hiddenLayerValue,
            },
          },
        };
      }

      return {
        ...current,
        baseEstimators: current.baseEstimators.map((estimator) =>
          estimator.id === estimatorId
            ? {
                ...estimator,
                algorithm: 'mlp',
                params: {
                  ...getAlgorithm('mlp').defaults,
                  ...estimator.params,
                  hidden_layer_sizes: hiddenLayerValue,
                },
              }
            : estimator,
        ),
      };
    });
    syncFormToAlgorithm('mlp');
  };

  const addDenseLayer = () => {
    const targetEstimator = selectedEstimator || stack.singleEstimator;
    const currentLayers = targetEstimator.algorithm === 'mlp'
      ? parsePositiveNumbers(targetEstimator.params.hidden_layer_sizes, [128, 64])
      : [128, 64];
    updateHiddenLayers(targetEstimator.id, [...currentLayers, 64]);
    setSelectedCanvasNodeId(null);
    setSelectedNodeId(targetEstimator.id);
    setIsInspectorOpen(true);
  };

  const removeDenseLayer = () => {
    const targetEstimator = selectedEstimator || stack.singleEstimator;
    const currentLayers = parsePositiveNumbers(targetEstimator.params.hidden_layer_sizes, [128, 64]);
    updateHiddenLayers(targetEstimator.id, currentLayers.length > 1 ? currentLayers.slice(0, -1) : currentLayers);
    setSelectedCanvasNodeId(null);
    setSelectedNodeId(targetEstimator.id);
    setIsInspectorOpen(true);
  };

  const setDenseLayerUnits = (estimatorId: string, layerIndex: number, value: string) => {
    const targetEstimator = [stack.singleEstimator, stack.metaLearner, ...stack.baseEstimators]
      .find((estimator) => estimator.id === estimatorId) || stack.singleEstimator;
    const currentLayers = parsePositiveNumbers(targetEstimator.params.hidden_layer_sizes, [128, 64]);
    const nextUnits = Math.max(Number(value) || 1, 1);
    updateHiddenLayers(
      estimatorId,
      currentLayers.map((units, index) => (index === layerIndex ? nextUnits : units)),
    );
  };

  const removeDenseLayerAt = (estimatorId: string, layerIndex: number) => {
    const targetEstimator = [stack.singleEstimator, stack.metaLearner, ...stack.baseEstimators]
      .find((estimator) => estimator.id === estimatorId) || stack.singleEstimator;
    const currentLayers = parsePositiveNumbers(targetEstimator.params.hidden_layer_sizes, [128, 64]);
    if (currentLayers.length <= 1) return;
    updateHiddenLayers(estimatorId, currentLayers.filter((_, index) => index !== layerIndex));
    setSelectedCanvasNodeId(null);
    setSelectedNodeId(estimatorId);
    setIsInspectorOpen(true);
  };

  const removeSelectedNode = () => {
    if (!selectedNodeId) return;
    setStack((current) => ({
      ...current,
      preprocessing: current.preprocessing.filter((step) => step.id !== selectedNodeId),
      baseEstimators: current.baseEstimators.filter((estimator) => estimator.id !== selectedNodeId),
    }));
    setSelectedCanvasNodeId(null);
    setSelectedNodeId(null);
  };

  const selectedPreprocessing = stack.preprocessing.find((step) => step.id === selectedNodeId) || null;
  const selectedEstimator = [
    stack.singleEstimator,
    stack.metaLearner,
    ...stack.baseEstimators,
  ].find((estimator) => estimator.id === selectedNodeId) || null;
  const editableEstimator = selectedEstimator || stack.singleEstimator;
  const editableDenseLayers = editableEstimator.algorithm === 'mlp'
    ? parsePositiveNumbers(editableEstimator.params.hidden_layer_sizes, [128, 64])
    : [];

  const modelLayers = useMemo(
    () => {
      const stackLayers = buildStackVisualizationLayers(form, stack);
      if (stackLayers.length > 0) return stackLayers;
      return (visualStackQuery.data?.layers || []) as Layer3DSpec[];
    },
    [form, stack, visualStackQuery.data?.layers],
  );
  const isCustomCodeVisualization = form.definitionMode === 'code'
    && modelLayers.length > 0
    && !hasSerializedArchitecture(stack);
  const hasCanvasContent = stack.preprocessing.length > 0 || modelLayers.length > 0;

  const flow = useMemo(() => {
    const nodes: CanvasNode[] = [];
    const edges: CanvasEdge[] = [];
    const addNode = (
      id: string,
      x: number,
      y: number,
      data: Omit<StackNodeData, 'selected'>,
    ) => {
      const isSelected = selectedCanvasNodeId
        ? id === selectedCanvasNodeId
        : id === selectedNodeId || data.sourceId === selectedNodeId;
      nodes.push({
        id,
        position: { x, y },
        selected: isSelected,
        data: { ...data, selected: isSelected },
      });
    };
    const addEdge = (source: string, target: string) => {
      edges.push({
        id: `${source}-${target}`,
        source,
        target,
      });
    };

    if (canvasView === 'pipeline') {
      if (!hasCanvasContent) {
        return { nodes, edges };
      }

      const pipelineBlocks: Array<{
        id: string;
        label: string;
        caption: string;
        tone: StackNodeData['tone'];
        sourceId?: string;
        details: string[];
      }> = [
        {
          id: 'pipeline_dataset',
          label: 'Dataset Source',
          caption: form.inputShape || 'tabular input',
          tone: 'input',
          details: [form.featureColumns || 'raw columns selected manually or by AI'],
        },
        ...stack.preprocessing.map((step) => ({
          id: step.id,
          label: step.label,
          caption: 'feature transform',
          tone: 'prep' as const,
          sourceId: step.id,
          details: [parameterSummary(step.params) || 'default parameters'],
        })),
        {
          id: 'pipeline_feature_tensor',
          label: 'Feature Tensor',
          caption: 'model input contract',
          tone: 'prep',
          details: [`${getFeatureCount(form.featureColumns)} features`, `target: ${form.targetName || 'target'}`],
        },
        {
          id: 'pipeline_model_stack',
          label: isCustomCodeVisualization ? 'Custom Python Model' : 'Model Stack',
          caption: isCustomCodeVisualization
            ? (form.customEntrypoint || 'custom code')
            : stack.mode === 'single'
              ? (hasConfiguredEstimator(stack.singleEstimator) ? getAlgorithmLabel(stack.singleEstimator.algorithm) : 'Not configured')
              : stack.baseEstimators.length > 0
                ? `${stack.mode} ensemble`
                : 'Not configured',
          tone: isCustomCodeVisualization ? 'model' : stack.mode === 'single' ? 'model' : 'ensemble',
          sourceId: isCustomCodeVisualization
            ? undefined
            : stack.mode === 'single'
              ? stack.singleEstimator.id
              : undefined,
          details: [modelLayers.length > 0 ? modelLayers.slice(1).map((layer) => layer.label).join(' -> ') : 'No model layers configured'],
        },
        {
          id: 'pipeline_prediction',
          label: 'Prediction Output',
          caption: form.problemType,
          tone: 'output',
          details: [`metric: ${form.objectiveMetric}`, `validation: ${normalizeValidationStrategy(form.validationStrategy)}`],
        },
      ];

      pipelineBlocks.forEach((block, index) => {
        addNode(block.id, 80 + index * 245, 120, block);
        if (index > 0) addEdge(pipelineBlocks[index - 1].id, block.id);
      });

      return { nodes, edges };
    }

    if (modelLayers.length === 0) {
      return { nodes, edges };
    }

    modelLayers.forEach((layer, index) => {
      const id = index === 0
        ? 'input'
        : index === modelLayers.length - 1
          ? 'output'
          : `model_layer_${index}`;
      const tone: StackNodeData['tone'] = index === 0
        ? 'input'
        : index === modelLayers.length - 1
          ? 'output'
          : layer.kind === 'preprocessing'
            ? 'prep'
            : layer.kind === 'ensemble' || layer.kind === 'vote'
              ? 'ensemble'
              : layer.kind === 'tree_ensemble' || layer.kind === 'aggregation'
                ? 'model'
                : layer.kind === 'linear_model'
                  ? 'meta'
                  : 'layer';

      addNode(id, 90 + index * 245, 115, {
        label: layer.label,
        caption: layer.kind || 'model layer',
        tone,
        sourceId: index > 0 && index < modelLayers.length - 1
            ? stack.mode === 'single'
              ? stack.singleEstimator.id
              : stack.mode === 'stacking' && !['ensemble', 'vote'].includes(String(layer.kind))
                ? stack.metaLearner.id
                : undefined
            : undefined,
        details: [
          `${layer.units} unit${layer.units === 1 ? '' : 's'}`,
          layer.detail || '',
        ].filter(Boolean),
      });

      if (index > 0) {
        const previousId = index - 1 === 0
          ? 'input'
          : index - 1 === modelLayers.length - 1
            ? 'output'
            : `model_layer_${index - 1}`;
        addEdge(previousId, id);
      }
    });

    return { nodes, edges };
  }, [canvasView, form, hasCanvasContent, isCustomCodeVisualization, modelLayers, selectedCanvasNodeId, selectedNodeId, stack]);

  const canvasTrail = canvasView === 'model'
    ? (modelLayers.length > 0 ? modelLayers.map((layer) => layer.label).join(' -> ') : 'Blank model canvas')
    : (hasCanvasContent
        ? [
            'Dataset Source',
            ...stack.preprocessing.map((step) => step.label),
            'Feature Tensor',
            'Model Stack',
            'Prediction Output',
          ].join(' -> ')
        : 'Blank pipeline canvas');
  const canvasKey = `${canvasView}-${stack.mode}-${flow.nodes.length}-${stack.preprocessing.length}`;
  const selectedCanvasNode = selectedCanvasNodeId
    ? flow.nodes.find((node) => node.id === selectedCanvasNodeId) || null
    : null;
  const selectedModelLayer = useMemo(() => {
    if (canvasView !== 'model' || !selectedCanvasNodeId) return null;
    if (selectedCanvasNodeId === 'input') return { layer: modelLayers[0], index: 0 };
    if (selectedCanvasNodeId === 'output') return { layer: modelLayers[modelLayers.length - 1], index: modelLayers.length - 1 };
    const match = selectedCanvasNodeId.match(/^model_layer_(\d+)$/);
    if (!match) return null;
    const index = Number(match[1]);
    const layer = modelLayers[index];
    return layer ? { layer, index } : null;
  }, [canvasView, modelLayers, selectedCanvasNodeId]);

  const createAssetMutation = useMutation<ModelLabAsset, Error, CreateAssetAction | undefined>({
    mutationFn: async (): Promise<ModelLabAsset> => {
      const runtimeOverrides = parseJsonInput<Record<string, any>>(form.runtimeOverridesJson, {});
      const architecture = serializeArchitecture(stack);
      const lifecyclePlan = serializeLifecyclePlan(lifecycle, form);
      const validationStrategy = normalizeValidationStrategy(form.validationStrategy);
      const canvasSnapshot = flow.nodes.length > 0
        ? {
            nodes: flow.nodes.map((node) => ({ id: node.id, position: node.position, data: node.data })),
            edges: flow.edges.map((edge) => ({ id: edge.id, source: edge.source, target: edge.target })),
          }
        : undefined;
      const customPythonAuthoring = {
        enabled: form.definitionMode === 'code',
        entrypoint: form.customEntrypoint.trim() || 'build_model',
        allowed_libraries: parseCommaList(form.customAllowedLibraries),
        code: form.customCode,
      };
      const noCodeAuthoring = {
        builder_template: 'model_stack_builder',
        builder_goal: form.builderGoal,
        input_shape: form.inputShape,
        feature_strategy: 'manual_or_ai_assisted',
        target_name: form.targetName,
        feature_columns: parseCommaList(form.featureColumns),
        preprocessing_steps: stack.preprocessing.map((step) => step.type),
        objective_metric: form.objectiveMetric,
        evaluation_metrics: parseCommaList(form.evaluationMetrics),
        validation_strategy: validationStrategy,
        output_schema: parseJsonInput(form.outputSchemaJson, {}),
        notes: form.notes,
        lifecycle_plan: lifecyclePlan,
        training_plan: lifecyclePlan.training,
        review_plan: lifecyclePlan.review,
        version_plan: lifecyclePlan.versions,
        testing_plan: lifecyclePlan.testing,
        inference_plan: lifecyclePlan.inference,
        ...(architecture ? { architecture } : {}),
      };
      const payload: ModelLabAssetCreate = {
        name: form.name.trim(),
        description: form.description || form.builderGoal,
        family: form.family,
        framework: form.framework,
        problem_type: form.problemType,
        validation_strategy: validationStrategy,
        tags: parseCommaList(form.tags),
        runtime_spec: {
          validation_strategy: validationStrategy,
          authoring_mode: form.definitionMode === 'code' ? 'custom_code' : 'model_stack_builder',
          definition_mode: form.definitionMode,
          executor: form.executor,
          authoring: {
            mode: form.definitionMode === 'code' ? 'custom_code' : 'model_stack_builder',
            no_code: noCodeAuthoring,
            python: customPythonAuthoring,
            stack_builder: {
              ...noCodeAuthoring,
              lifecycle: lifecyclePlan,
              ...(canvasSnapshot ? { canvas: canvasSnapshot } : {}),
            },
            lifecycle: lifecyclePlan,
          },
          lifecycle: lifecyclePlan,
          ...runtimeOverrides,
        },
      };

      if (draftAssetId) {
        return modelLabAssetApi.update(draftAssetId, payload);
      }

      return modelLabAssetApi.create(payload);
    },
    onSuccess: async (asset, action) => {
      await queryClient.invalidateQueries({ queryKey: ['model-lab-assets'] });
      setSavedAssetId(asset.id);
      if (!action?.operation) {
        toast.success(draftAssetId ? 'Model stack updated.' : 'Model stack saved to the registry.');
      }
      if (action?.operation) {
        setActiveOperation(action.operation);
      }
      if (!draftAssetId) {
        navigate(`/ai/ml/models/${asset.id}/edit`, { replace: true });
      }
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to create model asset.'));
    },
  });

  const isModelNameMissing = form.name.trim().length === 0;
  const isCreatingModel = createAssetMutation.isPending;
  const promptForModelName = () => {
    setIsSetupOpen(true);
    toast.info('Add a model name before creating or running model operations.');
  };
  const handleCreateModel = () => {
    if (isModelNameMissing) {
      promptForModelName();
      return;
    }
    createAssetMutation.mutate();
  };
  const openOperation = (operation: BuilderOperation) => {
    if (isModelNameMissing) {
      promptForModelName();
      return;
    }
    createAssetMutation.mutate({ operation });
  };
  const lifecycleActions = [
    {
      label: 'Start Training',
      icon: <PlayArrowIcon fontSize="small" />,
      operation: 'training' as const,
      action: () => openOperation('training'),
    },
    {
      label: 'Monitor Training',
      icon: <QueryStatsIcon fontSize="small" />,
      operation: 'jobs' as const,
      action: () => openOperation('jobs'),
    },
    {
      label: 'Review Results',
      icon: <InsightsIcon fontSize="small" />,
      operation: 'review' as const,
      action: () => openOperation('review'),
    },
    {
      label: 'Versions',
      icon: <ViewListIcon fontSize="small" />,
      operation: 'versions' as const,
      action: () => openOperation('versions'),
    },
    {
      label: 'Testing',
      icon: <ScienceIcon fontSize="small" />,
      operation: 'testing' as const,
      action: () => openOperation('testing'),
    },
    {
      label: 'Inference',
      icon: <RocketLaunchIcon fontSize="small" />,
      operation: 'inference' as const,
      action: () => openOperation('inference'),
    },
  ];

  const operationJobs = (builderJobsQuery.data?.results || []) as ModelLabTrainingJob[];
  const operationVersions = (builderVersionsQuery.data?.results || []) as ModelLabVersion[];
  const operationDeployments = (builderDeploymentsQuery.data?.results || []) as ModelLabDeployment[];
  const datasetVersions = (builderDatasetVersionsQuery.data?.results || []) as ModelLabDatasetVersion[];
  const featureSets = (builderFeatureSetsQuery.data?.results || []) as ModelLabFeatureSet[];
  const latestOperationVersion = [...operationVersions]
    .sort((a, b) => b.version_number - a.version_number)[0];
  const productionOperationVersion = operationVersions.find((version) => version.stage === 'PRODUCTION');

  const createTrainingJobMutation = useMutation<ModelLabTrainingJob, Error, { launch: boolean }>({
    mutationFn: async ({ launch }) => {
      if (!operationAssetId) {
        throw new Error('Save the model before creating a training job.');
      }
      if (!trainingForm.datasetVersionId) {
        throw new Error('Select a dataset version before creating a training job.');
      }

      const architecture = serializeArchitecture(stack);
      const featureColumns = parseCommaList(trainingForm.featureColumns || form.featureColumns);
      const singleEstimator = architecture?.single_estimator;
      const payload: ModelLabTrainingJobCreate = {
        model_asset: operationAssetId,
        dataset_version: Number(trainingForm.datasetVersionId),
        validation_strategy: normalizeValidationStrategy(form.validationStrategy),
        runtime_spec: {
          authoring_mode: form.definitionMode === 'code' ? 'custom_code' : 'model_stack_builder',
          executor: trainingForm.executor || form.executor,
          ...(trainingForm.computeSpecId ? { compute_spec_id: trainingForm.computeSpecId } : {}),
        },
        config: {
          target_column: trainingForm.targetColumn || form.targetName || 'target',
          validation_strategy: normalizeValidationStrategy(form.validationStrategy),
          test_size: Number(trainingForm.testSize || '0.25'),
          random_state: Number(trainingForm.randomState || '42'),
          ...(Number(trainingForm.cvFolds || '0') > 0 ? { cv_folds: Number(trainingForm.cvFolds) } : {}),
          ...(featureColumns.length > 0 ? { feature_columns: featureColumns } : {}),
          ...(architecture ? { architecture, model_type: architecture.model_type || stack.mode } : {}),
          ...(singleEstimator?.algorithm ? {
            algorithm: singleEstimator.algorithm,
            hyperparameters: singleEstimator.params || {},
          } : {}),
        },
        ...(trainingForm.featureSetId ? { feature_set: Number(trainingForm.featureSetId) } : {}),
      };

      const job = await modelLabTrainingJobApi.create(payload);
      if (launch) {
        return modelLabTrainingJobApi.launch(job.id, trainingForm.computeSpecId);
      }
      return job;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['model-lab-builder-jobs', operationAssetId] });
      await queryClient.invalidateQueries({ queryKey: ['model-lab-builder-versions', operationAssetId] });
      setActiveOperation('jobs');
      toast.success('Training job created.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to create training job.'));
    },
  });

  const operationPanelTitle = lifecycleActions.find((item) => item.operation === activeOperation)?.label || 'Operations';
  const completedJobs = operationJobs.filter((job) => job.status === 'COMPLETED');
  const failedJobs = operationJobs.filter((job) => job.status === 'FAILED');
  const runningJobs = operationJobs.filter((job) => job.status === 'RUNNING' || job.status === 'QUEUED');
  const operationPanel = activeOperation ? (
    <Paper
      square
      elevation={0}
      sx={{
        flex: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flexShrink: 1,
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {operationPanelTitle}
        </Typography>
        <Chip size="small" variant="outlined" label={operationAssetId ? `Model #${operationAssetId}` : 'Unsaved'} />
        <Button size="small" onClick={() => setActiveOperation(null)}>
          Close
        </Button>
      </Stack>
      <Divider />
      <Box sx={{ p: 2, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {activeOperation !== 'training' && (
          builderJobsQuery.isLoading || builderVersionsQuery.isLoading || builderDeploymentsQuery.isLoading
        ) ? <LinearProgress sx={{ mb: 1.5 }} /> : null}

        {activeOperation === 'training' && (
          <Stack spacing={1.5}>
            {(builderDatasetVersionsQuery.isLoading || builderFeatureSetsQuery.isLoading) && <LinearProgress />}
            <Alert severity="info" variant="outlined">
              Pick the dataset and target contract for this saved model. Creating and launching here keeps the workflow inside the builder.
            </Alert>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 1,
              }}
            >
              <TextField
                select
                label="Dataset version"
                size="small"
                value={trainingForm.datasetVersionId}
                onChange={(event) => setTrainingForm((current) => ({ ...current, datasetVersionId: event.target.value }))}
                required
              >
                {datasetVersions.length === 0 ? (
                  <MenuItem value="" disabled>No dataset versions available</MenuItem>
                ) : datasetVersions.map((version) => (
                  <MenuItem key={version.id} value={String(version.id)}>
                    {version.dataset_name || `Dataset #${version.dataset}`} - {version.name || `v${version.version_number}`}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Feature set"
                size="small"
                value={trainingForm.featureSetId}
                onChange={(event) => setTrainingForm((current) => ({ ...current, featureSetId: event.target.value }))}
              >
                <MenuItem value="">None</MenuItem>
                {featureSets.map((featureSet) => (
                  <MenuItem key={featureSet.id} value={String(featureSet.id)}>
                    {featureSet.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Target column"
                size="small"
                value={trainingForm.targetColumn}
                onChange={(event) => setTrainingForm((current) => ({ ...current, targetColumn: event.target.value }))}
                required
              />
              <TextField
                label="Feature columns"
                size="small"
                placeholder="open, high, low, close"
                value={trainingForm.featureColumns}
                onChange={(event) => setTrainingForm((current) => ({ ...current, featureColumns: event.target.value }))}
                helperText="Optional comma-separated override"
              />
              <TextField
                label="Executor"
                size="small"
                value={trainingForm.executor}
                onChange={(event) => setTrainingForm((current) => ({ ...current, executor: event.target.value }))}
              />
              <ComputeSpecSelector
                value={trainingForm.computeSpecId}
                onChange={(specId) => setTrainingForm((current) => ({ ...current, computeSpecId: specId }))}
                disabled={createTrainingJobMutation.isPending}
              />
              <TextField
                label="Test size"
                size="small"
                value={trainingForm.testSize}
                onChange={(event) => setTrainingForm((current) => ({ ...current, testSize: event.target.value }))}
              />
              <TextField
                label="Random state"
                size="small"
                value={trainingForm.randomState}
                onChange={(event) => setTrainingForm((current) => ({ ...current, randomState: event.target.value }))}
              />
              <TextField
                label="CV folds"
                size="small"
                value={trainingForm.cvFolds}
                onChange={(event) => setTrainingForm((current) => ({ ...current, cvFolds: event.target.value }))}
              />
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => createTrainingJobMutation.mutate({ launch: false })}
                disabled={createTrainingJobMutation.isPending}
              >
                Create Draft Job
              </Button>
              <Button
                variant="contained"
                startIcon={createTrainingJobMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon fontSize="small" />}
                onClick={() => createTrainingJobMutation.mutate({ launch: true })}
                disabled={createTrainingJobMutation.isPending}
              >
                Create & Launch
              </Button>
            </Stack>
          </Stack>
        )}

        {activeOperation === 'jobs' && (
          <TrainingJobCards jobs={operationJobs} />
        )}

        {activeOperation === 'review' && (
          <Stack spacing={1.5}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 1,
              }}
            >
              <BuilderInfoRow label="Completed jobs" value={completedJobs.length} />
              <BuilderInfoRow label="Failed jobs" value={failedJobs.length} />
              <BuilderInfoRow label="Latest version" value={latestOperationVersion ? `v${latestOperationVersion.version_number}` : 'None'} />
              <BuilderInfoRow label="Production" value={productionOperationVersion ? `v${productionOperationVersion.version_number}` : 'Not promoted'} />
            </Box>
            {latestOperationVersion ? (
              <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.paper' }}>
                <Typography variant="subtitle2" gutterBottom>Latest version metrics</Typography>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                  {formatMetricSummary(latestOperationVersion.metrics).length > 0
                    ? formatMetricSummary(latestOperationVersion.metrics).map((metric) => (
                        <Chip key={metric} size="small" variant="outlined" label={metric} />
                      ))
                    : <Typography variant="body2" color="text.secondary">No scalar metrics recorded.</Typography>}
                </Stack>
              </Paper>
            ) : (
              <EmptyOperationState title="Nothing to review yet" message="Launch training first, then review completed jobs and generated model versions here." />
            )}
          </Stack>
        )}

        {activeOperation === 'versions' && (
          <VersionCards versions={operationVersions} />
        )}

        {activeOperation === 'testing' && (
          <Stack spacing={1.5}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 1,
              }}
            >
              <BuilderInfoRow label="Runnable version" value={latestOperationVersion ? `v${latestOperationVersion.version_number}` : 'Missing'} />
              <BuilderInfoRow label="Production version" value={productionOperationVersion ? `v${productionOperationVersion.version_number}` : 'Missing'} />
              <BuilderInfoRow label="Active deployments" value={operationDeployments.filter((deployment) => deployment.is_active).length} />
              <BuilderInfoRow label="Recent failures" value={failedJobs.length} />
            </Box>
            {!latestOperationVersion ? (
              <Alert severity="warning" variant="outlined">
                Testing needs at least one generated model version. Start a training job first.
              </Alert>
            ) : (
              <Alert severity={failedJobs.length > 0 ? 'warning' : 'success'} variant="outlined">
                Builder checks found {operationVersions.length} version{operationVersions.length === 1 ? '' : 's'} and {operationDeployments.length} deployment{operationDeployments.length === 1 ? '' : 's'} for this model.
              </Alert>
            )}
            <ModelLabPredictionTestingPanel modelAssetId={operationAssetId} compact />
          </Stack>
        )}

        {activeOperation === 'inference' && (
          <Stack spacing={1.5}>
            <DeploymentCards deployments={operationDeployments} />
            {runningJobs.length > 0 ? (
              <Alert severity="info" variant="outlined">
                {runningJobs.length} training job{runningJobs.length === 1 ? '' : 's'} still running. New deployments should wait for the completed version.
              </Alert>
            ) : null}
          </Stack>
        )}
      </Box>
    </Paper>
  ) : null;

  const modelChatPreamble = currentModelAsset
    ? `Model Lab Asset (name: ${form.name || currentModelAsset.name}, ID: ${currentModelAsset.id})`
    : undefined;
  const modelChatContext = currentModelAsset
    ? {
        model_lab_asset_id: currentModelAsset.id,
        model_asset_id: currentModelAsset.id,
        model_name: form.name || currentModelAsset.name,
        model_problem_type: form.problemType,
        model_family: form.family,
        model_framework: form.framework,
        authoring_mode: form.definitionMode === 'code' ? 'custom_code' : 'model_stack_builder',
        definition_mode: form.definitionMode,
      }
    : undefined;
  const modelSessionQueryParams = useMemo(
    () => (currentModelAsset?.id
      ? {
          related_object_app_label: 'model_lab',
          related_object_model: 'modelasset',
          related_object_id: currentModelAsset.id,
        }
      : undefined),
    [currentModelAsset?.id],
  );

  const modelBuilderChatSubject = currentModelAsset
    ? { id: currentModelAsset.id, name: form.name || currentModelAsset.name }
    : { id: draftAssetId || 0, name: form.name || 'New Model Lab asset' };

  const modelSetupContent = (
    <Stack spacing={2} sx={{ py: 0.5 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: 1.25,
          alignItems: 'start',
        }}
      >
        <TextField
          label="Model name"
          size="small"
          value={form.name}
          onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          required
          error={isModelNameMissing}
          helperText={isModelNameMissing ? 'Required' : undefined}
          autoFocus
        />
        <TextField
          label="Goal"
          size="small"
          value={form.builderGoal}
          onChange={(event) => setForm((current) => ({ ...current, builderGoal: event.target.value }))}
        />
        <TextField
          select
          label="Problem"
          size="small"
          value={form.problemType}
          onChange={(event) =>
            setForm((current) => ({ ...current, problemType: event.target.value as ModelLabProblemType }))
          }
        >
          <MenuItem value="CLASSIFICATION">Classification</MenuItem>
          <MenuItem value="REGRESSION">Regression</MenuItem>
          <MenuItem value="TIME_SERIES">Time Series</MenuItem>
          <MenuItem value="REINFORCEMENT">Reinforcement</MenuItem>
        </TextField>
        <TextField
          label="Description"
          size="small"
          value={form.description}
          onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
          placeholder="Brief model purpose, scope, and deployment context"
          multiline
          minRows={2}
          sx={{ gridColumn: '1 / -1' }}
        />
      </Box>

      <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2">Authoring mode</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {getAuthoringModeHelper(form)}
            </Typography>
          </Box>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={form.definitionMode}
            onChange={(_, value: BuilderFormState['definitionMode'] | null) => {
              if (value) setForm((current) => ({ ...current, definitionMode: value }));
            }}
            sx={{ flexShrink: 0 }}
          >
            <ToggleButton value="stack">Stack builder</ToggleButton>
            <ToggleButton value="code">Custom Python</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      <Divider flexItem />
      <Stack direction="column" spacing={1.25} alignItems="stretch">
        <Stack direction="row" spacing={1} alignItems="center">
          <StorageIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Contract</Typography>
        </Stack>
        <Box
          sx={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 1.25,
            minWidth: 0,
            alignItems: 'start',
          }}
        >
          <TextField
            label="Target"
            size="small"
            value={form.targetName}
            onChange={(event) => setForm((current) => ({ ...current, targetName: event.target.value }))}
          />
          <TextField
            label="Features"
            size="small"
            placeholder="open, high, low, volume"
            value={form.featureColumns}
            onChange={(event) => setForm((current) => ({ ...current, featureColumns: event.target.value }))}
          />
          <TextField
            select
            label="Metric"
            size="small"
            value={form.objectiveMetric}
            onChange={(event) => setForm((current) => ({ ...current, objectiveMetric: event.target.value }))}
          >
            <MenuItem value="accuracy">Accuracy</MenuItem>
            <MenuItem value="f1_weighted">F1 weighted</MenuItem>
            <MenuItem value="mse">MSE</MenuItem>
            <MenuItem value="mae">MAE</MenuItem>
            <MenuItem value="r2">R2</MenuItem>
            <MenuItem value="episode_reward">Episode reward</MenuItem>
          </TextField>
          <TextField
            select
            label="Validation"
            size="small"
            value={normalizeValidationStrategy(form.validationStrategy)}
            onChange={(event) => setForm((current) => ({
              ...current,
              validationStrategy: normalizeValidationStrategy(event.target.value),
            }))}
          >
            {VALIDATION_STRATEGY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Executor"
            size="small"
            value={form.executor}
            onChange={(event) => {
              const executor = event.target.value;
              setForm((current) => ({ ...current, executor }));
              setLifecycle((current) => ({
                ...current,
                training: { ...current.training, computeTarget: executor },
              }));
            }}
          >
            <MenuItem value="cpu">CPU</MenuItem>
            <MenuItem value="gpu">GPU</MenuItem>
            <MenuItem value="distributed">Distributed</MenuItem>
          </TextField>
        </Box>
      </Stack>
    </Stack>
  );

  const modelLabAgentSelector = modelLabAgentOptions.length > 0 ? (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      <AgentAutocomplete
        value={selectedAgentId}
        onChange={(agentId) => setSelectedAgentId(agentId)}
        label="Model Lab agent"
        required
        filters={{ is_active: true, agent_type: 'MODEL_LAB_BUILDER' }}
        sx={{
          width: { sm: 168, md: 210 },
          minWidth: 0,
          maxWidth: '100%',
          display: { xs: 'none', sm: 'block' },
          '& .MuiInputBase-root': { height: 30 },
          '& .MuiInputBase-input': { py: 0.25, fontSize: '0.78rem' },
        }}
      />
      {selectedAgentId && (
        <Tooltip title="Open selected agent">
          <IconButton
            size="small"
            onClick={() => navigate(`/ai/agents/${selectedAgentId}`)}
            aria-label="Open selected Model Lab agent"
            sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          >
            <SettingsIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  ) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
        m: 0,
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <Paper
        square
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: { xs: 1.5, md: 2.5 },
          py: 1,
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <PageBackButton label="Back to Registry" to="/ai/ml/models" preferHistory={false} hideLabelOnMobile />
        <HubIcon color="primary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap sx={{ lineHeight: 1.2 }}>
            Model Builder
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            AI-assisted model stack design for Model Lab assets
          </Typography>
        </Box>
        <Tooltip title="Open model setup">
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setIsSetupOpen(true)}
            sx={{ flexShrink: 0 }}
          >
            Setup
          </Button>
        </Tooltip>
        <Button
          variant={isInspectorOpen ? 'contained' : 'outlined'}
          startIcon={<ViewListIcon />}
          onClick={() => setIsInspectorOpen((open) => !open)}
          sx={{ flexShrink: 0 }}
        >
          Inspector
        </Button>
        <Button
          variant={isModelNameMissing ? 'outlined' : 'contained'}
          startIcon={isCreatingModel ? <CircularProgress size={16} color="inherit" /> : isModelNameMissing ? <SettingsIcon /> : <AddIcon />}
          onClick={handleCreateModel}
          disabled={isCreatingModel}
        >
          {isModelNameMissing ? 'Complete Setup' : draftAssetId ? 'Save Model' : 'Create Model'}
        </Button>
      </Paper>

      <Dialog
        open={isSetupOpen}
        onClose={() => setIsSetupOpen(false)}
        fullWidth
        maxWidth="md"
        fullScreen={!isDesktop}
        aria-labelledby="model-setup-dialog-title"
      >
        <DialogTitle
          id="model-setup-dialog-title"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}
        >
          <SettingsIcon color="primary" fontSize="small" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="div" noWrap>
              Model setup
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {(form.name.trim() || 'Untitled model')} · {form.problemType.replace('_', ' ')} · {form.objectiveMetric} · {form.executor.toUpperCase()}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} sx={{ ml: 'auto', mr: 3, display: { xs: 'none', sm: 'flex' } }}>
            <Chip size="small" color={form.definitionMode === 'code' ? 'warning' : 'primary'} label={getAuthoringModeLabel(form)} variant="outlined" />
            <Chip
              size="small"
              label={form.definitionMode === 'code' && form.customCode.trim() ? 'Custom code present' : form.definitionMode === 'code' ? 'Code empty' : 'System stack'}
              variant="outlined"
            />
          </Stack>
          <IconButton
            aria-label="Close model setup"
            onClick={() => setIsSetupOpen(false)}
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {modelSetupContent}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setIsSetupOpen(false)}>Close</Button>
          <Button
            variant={isModelNameMissing ? 'outlined' : 'contained'}
            startIcon={isCreatingModel ? <CircularProgress size={16} color="inherit" /> : isModelNameMissing ? <SettingsIcon /> : <AddIcon />}
            onClick={handleCreateModel}
            disabled={isCreatingModel}
          >
            {isModelNameMissing ? 'Complete Setup' : draftAssetId ? 'Save Model' : 'Create Model'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        ref={shellRef}
        sx={{
          flex: 1,
          minHeight: 0,
          display: isDesktop ? 'grid' : 'flex',
          gridTemplateColumns: isDesktop
            ? isInspectorOpen
              ? `${chatPaneWidth}px 8px minmax(${CANVAS_PANE_MIN_WIDTH}px, 1fr) 8px ${inspectorPaneWidth}px`
              : `${chatPaneWidth}px 8px minmax(${CANVAS_PANE_MIN_WIDTH}px, 1fr)`
            : undefined,
          flexDirection: isDesktop ? undefined : 'column',
          overflow: 'hidden',
        }}
      >
        <Paper
          square
          elevation={0}
          sx={{
            minHeight: isDesktop ? 0 : '46vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          {isManagerAgentLoading && <LinearProgress />}
          {builderAssetQuery.isLoading && <LinearProgress color="secondary" />}
          {!isManagerAgentLoading && !managerAgent && (
            <Box p={2}>
              <Alert severity="warning" action={
                <Button size="small" color="inherit" onClick={() => managerAgentQuery.refetch()}>
                  Retry
                </Button>
              }>
                Model Lab Builder agent is not available for this user. Create or provision a MODEL_LAB_BUILDER template to enable the chat manager here.
              </Alert>
            </Box>
          )}
          {builderAssetQuery.error && (
            <Box p={2}>
              <Alert severity="error">
                Failed to load the model asset for this builder session.
              </Alert>
            </Box>
          )}
          {managerAgent && (!draftAssetId || currentModelAsset) && (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <AIBuilder
                key={`${managerAgent.id}:${currentModelAsset?.id ?? draftAssetId ?? 'new'}`}
                system={modelBuilderChatSubject}
                agent={managerAgent as any}
                chatOnly
                chatTitle="AI Manager"
                assistantLabel="AI Manager"
                emptyStateTitle="Start building this model"
                emptyStateHint="Ask for a model stack, neural architecture, training plan, tests, versions, or custom Python definition."
                inputPlaceholder="Ask AI Manager to help build this model..."
                initialMessagePrefix={modelChatPreamble}
                executionSystemId={null}
                sessionAiSystemId={null}
                sessionQueryParams={modelSessionQueryParams}
                autoLoadLatestSession={Boolean(currentModelAsset?.id)}
                enableSessionHistory={Boolean(currentModelAsset?.id)}
                enableSnapshots={false}
                headerActions={modelLabAgentSelector}
                onExpertCreated={handleAgentConversationCompleted}
                executionContext={modelChatContext}
                refreshToolNames={MODEL_LAB_REALTIME_REFRESH_TOOLS}
              />
            </Box>
          )}
        </Paper>

        {isDesktop && <PaneResizeHandle onMouseDown={startPaneResize('chat')} />}

        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'background.default',
          }}
        >
          <Paper square elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} alignItems={{ xs: 'stretch', lg: 'center' }} sx={{ px: 2, py: 1 }}>
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  gap: 0.75,
                  minWidth: 0,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': {
                    height: 6,
                  },
                }}
              >
                {lifecycleActions.map((item) => (
                  <Tooltip key={item.label} title={item.label}>
                    <span>
                      <Button
                        size="small"
                        variant={activeOperation ? (activeOperation === item.operation ? 'contained' : 'outlined') : item.label === 'Start Training' ? 'contained' : 'outlined'}
                        startIcon={item.icon}
                        onClick={item.action}
                        disabled={isCreatingModel}
                        sx={{
                          flexShrink: 0,
                          justifyContent: 'flex-start',
                          minHeight: 34,
                          minWidth: 0,
                          px: 1,
                          textAlign: 'left',
                          '& .MuiButton-startIcon': {
                            flexShrink: 0,
                            mr: 0.75,
                          },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            minWidth: 0,
                            display: 'block',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.label}
                        </Box>
                      </Button>
                    </span>
                  </Tooltip>
                ))}
              </Box>
            </Stack>
          </Paper>

          {operationPanel}

          {!activeOperation && (
            <>
          <Paper square elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ px: 2, py: 0.75 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  Model workspace
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ display: 'none' }}>
                  {form.definitionMode === 'code' ? `${form.customEntrypoint || 'build_model'} · Python` : 'Visual stack builder'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {workspaceTab === 'canvas'
                    ? canvasTrail
                    : workspaceTab === 'analysis'
                      ? selectedAnalysisVersion
                        ? `Data analysis - ${selectedAnalysisVersion.name}`
                        : 'Data analysis'
                    : workspaceTab === 'three_d'
                      ? `${modelLayers.length} architecture block${modelLayers.length === 1 ? '' : 's'}`
                      : form.definitionMode === 'code'
                        ? `${form.customEntrypoint || 'build_model'} - Python`
                        : 'Visual stack builder'}
                </Typography>
              </Box>
              <Chip
                size="small"
                color={form.definitionMode === 'code' ? 'warning' : 'primary'}
                variant="outlined"
                label={getAuthoringModeLabel(form)}
                sx={{ display: { xs: 'none', lg: 'inline-flex' }, flexShrink: 0 }}
              />
              <Tabs
                value={workspaceTab}
                onChange={(_, value: BuilderWorkspaceTab) => setWorkspaceTab(value)}
                sx={{
                  flexShrink: 0,
                  minHeight: 34,
                  '& .MuiTab-root': {
                    minHeight: 34,
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: 'text.secondary',
                  },
                  '& .Mui-selected': {
                    color: 'primary.main',
                  },
                }}
              >
                <Tab value="canvas" label="Canvas" />
                <Tab value="analysis" label="Analysis" />
                <Tab value="three_d" label="3D" />
                <Tab value="code" label="Code" />
              </Tabs>
            </Stack>
            <Collapse in={false} timeout="auto" unmountOnExit>
              <Divider />
              <Stack spacing={1} sx={{ px: 2, py: 1.25 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 240px) minmax(260px, 1fr)' },
                    gap: 1,
                  }}
                >
                  <TextField
                    label="Entrypoint"
                    size="small"
                    value={form.customEntrypoint}
                    onChange={(event) => setForm((current) => ({ ...current, customEntrypoint: event.target.value, definitionMode: 'code' }))}
                  />
                  <TextField
                    label="Libraries"
                    size="small"
                    value={form.customAllowedLibraries}
                    onChange={(event) => setForm((current) => ({ ...current, customAllowedLibraries: event.target.value, definitionMode: 'code' }))}
                  />
                </Box>
                <TextField
                  label="Custom Python model"
                  value={form.customCode}
                  onFocus={() => setForm((current) => ({ ...current, definitionMode: 'code' }))}
                  onChange={(event) => setForm((current) => ({ ...current, customCode: event.target.value, definitionMode: 'code' }))}
                  multiline
                  minRows={isDesktop ? 20 : 14}
                  fullWidth
                  InputProps={{
                    sx: {
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: 13,
                      alignItems: 'flex-start',
                    },
                  }}
                />
              </Stack>
            </Collapse>
          </Paper>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              bgcolor: 'background.default',
            }}
          >
            <Stack spacing={0} sx={{ height: '100%', minHeight: 0 }}>
            {workspaceTab === 'canvas' && (
            <Paper
              square
              elevation={0}
              sx={{
                flex: 1,
                minHeight: 0,
                height: isCanvasFullscreen ? '100vh' : '100%',
                display: 'flex',
                flexDirection: 'column',
                borderBottom: isCanvasFullscreen ? 0 : 1,
                borderColor: 'divider',
                position: 'relative',
                ...(isCanvasFullscreen
                  ? {
                      position: 'fixed',
                      inset: 0,
                      zIndex: theme.zIndex.modal + 2,
                      bgcolor: 'background.paper',
                      boxShadow: 24,
                    }
                  : {}),
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                useFlexGap
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 10,
                  zIndex: 3,
                  px: 0,
                  py: 0,
                  borderBottom: 0,
                  borderColor: 'transparent',
                  bgcolor: 'transparent',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  pointerEvents: 'none',
                }}
              >
                <AccountTreeIcon fontSize="small" color="primary" sx={{ display: 'none' }} />
                <Box sx={{ display: 'none' }}>
                  <Typography variant="subtitle2">
                    {canvasView === 'model' ? 'Model Stack Canvas' : 'Data Pipeline Canvas'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {canvasTrail}
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={canvasView}
                  onChange={(_, nextView: CanvasView | null) => {
                    if (nextView) switchCanvasView(nextView);
                  }}
                  sx={{
                    flexShrink: 0,
                    pointerEvents: 'auto',
                    '& .MuiToggleButton-root': {
                      px: 1.25,
                      py: 0.5,
                      color: 'text.secondary',
                      borderColor: 'divider',
                    },
                    '& .Mui-selected': {
                      color: 'primary.contrastText',
                      bgcolor: 'primary.main',
                      '&:hover': { bgcolor: 'primary.dark' },
                    },
                  }}
                >
                  <ToggleButton value="model">Model Stack</ToggleButton>
                  <ToggleButton value="pipeline">Data Pipeline</ToggleButton>
                </ToggleButtonGroup>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ml: 'auto', pointerEvents: 'auto' }}>
                  <Tooltip title="Zoom out">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setCanvasZoomClamped((current) => current - CANVAS_ZOOM_STEP)}
                        disabled={canvasZoom <= CANVAS_MIN_ZOOM}
                      >
                        <ZoomOutIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Chip size="small" label={`${Math.round(canvasZoom * 100)}%`} variant="outlined" sx={{ minWidth: 58 }} />
                  <Tooltip title="Zoom in">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setCanvasZoomClamped((current) => current + CANVAS_ZOOM_STEP)}
                        disabled={canvasZoom >= CANVAS_MAX_ZOOM}
                      >
                        <ZoomInIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Reset view">
                    <IconButton size="small" onClick={resetCanvasViewport}>
                      <CenterFocusStrongIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={isCanvasFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                    <IconButton size="small" onClick={() => setIsCanvasFullscreen((current) => !current)}>
                      {isCanvasFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              <Box
                key={canvasKey}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  height: 0,
                  overflow: 'hidden',
                  backgroundColor: 'background.default',
                  backgroundImage: `radial-gradient(${theme.palette.divider} 1px, transparent 1px)`,
                  backgroundSize: '18px 18px',
                  cursor: isCanvasPanning ? 'grabbing' : 'grab',
                  touchAction: 'none',
                  overscrollBehavior: 'contain',
                  userSelect: isCanvasPanning ? 'none' : 'auto',
                }}
                onWheelCapture={handleCanvasWheel}
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={stopCanvasPan}
                onMouseLeave={stopCanvasPan}
                onClick={() => {
                  setSelectedNodeId(null);
                  setSelectedCanvasNodeId(null);
                }}
              >
                <Box
                  sx={{
                    width: 'max-content',
                    minHeight: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    px: 4,
                    py: 3,
                    transform: `translate(${canvasPan.x}px, ${canvasPan.y}px) scale(${canvasZoom})`,
                    transformOrigin: '0 50%',
                    transition: isCanvasPanning ? 'none' : 'transform 120ms ease',
                  }}
                >
                  {flow.nodes.length === 0 ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        minWidth: 320,
                        px: 3,
                        py: 2,
                        borderStyle: 'dashed',
                        textAlign: 'center',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Typography variant="subtitle2">
                        {canvasView === 'model' ? 'Blank model canvas' : 'Blank pipeline canvas'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        Ask AI Manager to build the first block, or use the inspector to add layers and transforms manually.
                      </Typography>
                    </Paper>
                  ) : flow.nodes.map((node, index) => {
                    const data = node.data as StackNodeData;
                    const tone = NODE_TONES[data.tone];
                    const isSelected = selectedCanvasNodeId
                      ? node.id === selectedCanvasNodeId
                      : node.id === selectedNodeId || data.sourceId === selectedNodeId;
                    return (
                      <React.Fragment key={node.id}>
                        <Paper
                          data-model-canvas-node="true"
                          elevation={isSelected ? 8 : 2}
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedCanvasNodeId(node.id);
                            setSelectedNodeId(data.sourceId || node.id);
                            setIsInspectorOpen(true);
                          }}
                          sx={{
                            width: 230,
                            minHeight: 118,
                            border: '1px solid',
                            borderColor: isSelected ? tone.color : 'divider',
                            borderLeft: `4px solid ${tone.color}`,
                            bgcolor: 'background.paper',
                            cursor: 'pointer',
                            overflow: 'hidden',
                          }}
                        >
                          <Box sx={{ px: 1.5, py: 1, bgcolor: tone.bg }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone.color, flexShrink: 0 }} />
                              <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                                {data.label}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {data.caption}
                            </Typography>
                          </Box>
                          <Stack spacing={0.5} sx={{ px: 1.5, py: 1 }}>
                            {(data.details || []).slice(0, 3).map((detail) => (
                              <Typography key={detail} variant="caption" color="text.secondary" noWrap>
                                {detail}
                              </Typography>
                            ))}
                          </Stack>
                        </Paper>
                        {index < flow.nodes.length - 1 && (
                          <Box
                            sx={{
                              width: 64,
                              height: 2,
                              bgcolor: 'primary.main',
                              mx: 1.5,
                              position: 'relative',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                right: -1,
                                top: -4,
                                borderTop: '5px solid transparent',
                                borderBottom: '5px solid transparent',
                                borderLeft: `8px solid ${theme.palette.primary.main}`,
                              },
                            }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Box>
            </Paper>
            )}

            {workspaceTab === 'analysis' && (
            <Paper square elevation={0} sx={{ flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                  alignItems: 'stretch',
                  minWidth: 0,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <QueryStatsIcon fontSize="small" color="primary" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2">Data analysis</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: { xs: 'normal', md: 'nowrap' },
                      }}
                    >
                      Raw data, engineered features, distributions, correlations, target balance, and time-series checks.
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))',
                    gap: 1,
                    alignItems: 'start',
                    minWidth: 0,
                    '& .MuiFormHelperText-root': { minHeight: 18, m: '3px 0 0 0' },
                  }}
                >
                  <Autocomplete
                    size="small"
                    options={analysisDatasetVersionOptions}
                    value={selectedAnalysisVersion}
                    loading={analysisDatasetVersionsQuery.isFetching}
                    filterOptions={(options) => options}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.dataset_name}
                    onChange={(_, value) => {
                      setAnalysisDatasetVersionId(value ? String(value.id) : '');
                      setAnalysisTargetColumn('');
                    }}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input' || reason === 'clear') {
                        setAnalysisDatasetSearch(value);
                        setAnalysisDatasetPage(1);
                        setAnalysisDatasetVersionOptions([]);
                      }
                    }}
                    ListboxProps={{
                      style: { maxHeight: 320, overflow: 'auto' },
                      onScroll: (event) => {
                        const list = event.currentTarget;
                        const nearBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 32;
                        if (nearBottom && analysisDatasetHasMore && !analysisDatasetVersionsQuery.isFetching) {
                          setAnalysisDatasetPage((page) => page + 1);
                        }
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Dataset"
                        fullWidth
                        helperText={analysisDatasetVersionsQuery.data?.count != null ? `${analysisDatasetVersionsQuery.data.count} active datasets` : undefined}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap>{option.dataset_name}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            Latest: {option.name} - v{option.version_number} - {option.record_count.toLocaleString()} rows
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                  <TextField
                    select
                    size="small"
                    label="Feature set"
                    value={analysisFeatureSetId}
                    onChange={(event) => {
                      setAnalysisFeatureSetId(event.target.value);
                      setAnalysisTargetColumn('');
                    }}
                    disabled={!selectedAnalysisVersionId || analysisFeatureSetsQuery.isFetching}
                    fullWidth
                    helperText={
                      analysisFeatureSetsQuery.isFetching
                        ? 'Loading features'
                        : analysisFeatureSets.length
                          ? `${analysisFeatureSets.length} active feature sets`
                          : 'Raw dataset columns'
                    }
                  >
                    <MenuItem value="">Raw dataset</MenuItem>
                    {analysisFeatureSets.map((featureSet) => (
                      <MenuItem key={featureSet.id} value={String(featureSet.id)}>
                        {featureSet.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Target"
                    value={analysisTargetColumn}
                    onChange={(event) => setAnalysisTargetColumn(event.target.value)}
                    helperText={analysisTargetColumn ? 'Selected label' : 'Optional'}
                    fullWidth
                  >
                    <MenuItem value="">No target</MenuItem>
                    {analysisColumns.map((column) => (
                      <MenuItem key={column} value={column}>{column}</MenuItem>
                    ))}
                  </TextField>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<QueryStatsIcon />}
                    onClick={() => datasetAnalysisQuery.refetch()}
                    disabled={!selectedAnalysisVersionId || datasetAnalysisQuery.isFetching}
                    fullWidth
                    sx={{ height: 40, minWidth: 0 }}
                  >
                    Analyze
                  </Button>
                </Box>
              </Box>
              {datasetAnalysisQuery.isFetching && <LinearProgress />}
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
                {!selectedAnalysisVersionId && (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="subtitle2">Select a dataset version</Typography>
                    <Typography variant="body2" color="text.secondary">
                      The analysis report will use stored dataset rows and schema metadata.
                    </Typography>
                  </Box>
                )}
                {datasetAnalysisQuery.error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {getApiErrorMessage(datasetAnalysisQuery.error)}
                  </Alert>
                )}
                {queuedAnalysisJob && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Analysis queued as data job #{queuedAnalysisJob.data_processing_job.id}
                    {queuedAnalysisJob.model_lab_run?.id ? ` on run #${queuedAnalysisJob.model_lab_run.id}` : ''}.
                  </Alert>
                )}
                {datasetAnalysisReport && (
                  <AdvancedAnalysisReport
                    report={datasetAnalysisReport}
                    selectedFeatureSet={selectedAnalysisFeatureSet}
                    isFetching={datasetAnalysisQuery.isFetching}
                  />
                )}
              </Box>
            </Paper>
            )}

            {workspaceTab === 'three_d' && (
            <Paper square elevation={0} sx={{ flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <InsightsIcon fontSize="small" color="secondary" />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle2">Neural Network Visualization</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {modelLayers.length > 0 ? 'Live architecture preview from the current model stack' : 'No neural blocks configured yet'}
                  </Typography>
                </Box>
                <Chip size="small" label={`${modelLayers.length} blocks`} variant="outlined" />
              </Stack>
              <Box sx={{ flex: 1, minHeight: 0, p: 1.25, overflow: 'auto' }}>
                {modelLayers.length > 0 ? (
                  <NeuralNetwork3D layers={modelLayers} height={isDesktop ? 560 : 420} />
                ) : (
                  <Box
                    sx={{
                      height: isDesktop ? 560 : 420,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      border: 1,
                      borderColor: 'divider',
                      borderStyle: 'dashed',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      px: 3,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2">Blank architecture</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        Add a model stack first to generate the neural network preview.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Paper>
            )}

            {workspaceTab === 'code' && (
            <Paper
              square
              elevation={0}
              sx={{
                flex: 1,
                minHeight: 0,
                height: isCodeFullscreen ? '100vh' : '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                ...(isCodeFullscreen
                  ? {
                      position: 'fixed',
                      inset: 0,
                      zIndex: theme.zIndex.modal + 2,
                      bgcolor: 'background.paper',
                      boxShadow: 24,
                    }
                  : {}),
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>Code editor</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {form.customEntrypoint || 'build_model'} - Python
                  </Typography>
                </Box>
                <TextField
                  label="Entrypoint"
                  size="small"
                  value={form.customEntrypoint}
                  onChange={(event) => setForm((current) => ({ ...current, customEntrypoint: event.target.value, definitionMode: 'code' }))}
                  sx={{ width: { xs: '100%', md: 190 } }}
                />
                <TextField
                  label="Libraries"
                  size="small"
                  value={form.customAllowedLibraries}
                  onChange={(event) => setForm((current) => ({ ...current, customAllowedLibraries: event.target.value, definitionMode: 'code' }))}
                  sx={{ width: { xs: '100%', md: 260 } }}
                />
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={form.definitionMode}
                  onChange={(_, value: BuilderFormState['definitionMode'] | null) => {
                    if (value) setForm((current) => ({ ...current, definitionMode: value }));
                  }}
                >
                  <ToggleButton value="stack">Stack</ToggleButton>
                  <ToggleButton value="code">Code</ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title={isCodeFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                  <IconButton size="small" onClick={() => setIsCodeFullscreen((current) => !current)}>
                    {isCodeFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Stack>
              <Box
                component="textarea"
                aria-label="Custom Python model code"
                value={form.customCode}
                onFocus={() => setForm((current) => ({ ...current, definitionMode: 'code' }))}
                onChange={(event) => setForm((current) => ({ ...current, customCode: event.currentTarget.value, definitionMode: 'code' }))}
                spellCheck={false}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                  resize: 'none',
                  overflow: 'auto',
                  border: 0,
                  outline: 0,
                  p: 2,
                  bgcolor: 'background.default',
                  color: 'text.primary',
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  fontSize: 13,
                  lineHeight: 1.55,
                }}
              />
            </Paper>
            )}
            </Stack>
          </Box>
            </>
          )}
        </Box>

        {isDesktop && isInspectorOpen && <PaneResizeHandle onMouseDown={startPaneResize('inspector')} />}

        {isInspectorOpen && <Paper
          square
          elevation={0}
          sx={{
            minHeight: isDesktop ? 0 : '48vh',
            borderTop: isDesktop ? 0 : 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Stack spacing={2} sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SettingsIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">Stack Structure</Typography>
              </Stack>

              <TextField
                select
                label="Stack type"
                size="small"
                value={stack.mode}
                onChange={(event) => {
                  const nextMode = event.target.value as ArchitectureMode;
                  setStack((current) => ({ ...current, mode: nextMode }));
                  setSelectedCanvasNodeId(null);
                  setSelectedNodeId(nextMode === 'single' ? stack.singleEstimator.id : stack.baseEstimators[0]?.id ?? null);
                }}
                fullWidth
              >
                <MenuItem value="single">Single model</MenuItem>
                <MenuItem value="voting">Voting ensemble</MenuItem>
                <MenuItem value="stacking">Stacked ensemble</MenuItem>
              </TextField>

              {stack.mode === 'single' && (
                <TextField
                  select
                  label="Primary algorithm"
                  size="small"
                  value={stack.singleEstimator.algorithm || ''}
                  displayEmpty
                  renderValue={(value) => value ? getAlgorithmLabel(String(value)) : 'Select algorithm'}
                  onChange={(event) => setEstimatorAlgorithm(stack.singleEstimator.id, event.target.value)}
                  onFocus={() => {
                    setSelectedCanvasNodeId(null);
                    setSelectedNodeId(stack.singleEstimator.id);
                    setIsInspectorOpen(true);
                  }}
                  fullWidth
                >
                  <MenuItem value="" disabled>Select algorithm</MenuItem>
                  {visibleAlgorithms.map((algorithm) => (
                    <MenuItem key={algorithm.value} value={algorithm.value}>
                      {algorithm.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {stack.mode === 'voting' && (
                <TextField
                  select
                  label="Voting strategy"
                  size="small"
                  value={stack.votingStrategy}
                  onChange={(event) =>
                    setStack((current) => ({ ...current, votingStrategy: event.target.value as 'hard' | 'soft' }))
                  }
                  fullWidth
                >
                  <MenuItem value="hard">Hard vote</MenuItem>
                  <MenuItem value="soft">Soft vote</MenuItem>
                </TextField>
              )}

              {stack.mode !== 'single' && (
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="overline" color="text.secondary" sx={{ flex: 1 }}>
                      Base models
                    </Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={addBaseEstimator}>
                      Add
                    </Button>
                  </Stack>
                  {stack.baseEstimators.map((estimator) => (
                    <Stack key={estimator.id} direction="row" spacing={0.75} alignItems="center">
                      <TextField
                        select
                        size="small"
                        value={estimator.algorithm}
                        onChange={(event) => setEstimatorAlgorithm(estimator.id, event.target.value)}
                        onFocus={() => {
                          setSelectedCanvasNodeId(null);
                          setSelectedNodeId(estimator.id);
                          setIsInspectorOpen(true);
                        }}
                        fullWidth
                      >
                        {visibleAlgorithms.map((algorithm) => (
                          <MenuItem key={algorithm.value} value={algorithm.value}>
                            {algorithm.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Tooltip title="Select base model">
                        <IconButton size="small" onClick={() => {
                          setSelectedCanvasNodeId(null);
                          setSelectedNodeId(estimator.id);
                          setIsInspectorOpen(true);
                        }}>
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                  {stack.mode === 'stacking' && (
                    <TextField
                      select
                      label="Meta learner"
                      size="small"
                      value={stack.metaLearner.algorithm}
                      onChange={(event) => setEstimatorAlgorithm(stack.metaLearner.id, event.target.value)}
                      onFocus={() => {
                        setSelectedCanvasNodeId(null);
                        setSelectedNodeId(stack.metaLearner.id);
                        setIsInspectorOpen(true);
                      }}
                      fullWidth
                    >
                      {visibleAlgorithms.map((algorithm) => (
                        <MenuItem key={algorithm.value} value={algorithm.value}>
                          {algorithm.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Stack>
              )}

              <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default' }}>
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2">Neural Layers</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        Editing {editableEstimator.id === stack.singleEstimator.id ? 'primary model' : 'selected model'}
                      </Typography>
                    </Box>
                    <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={addDenseLayer}>
                      Add
                    </Button>
                  </Stack>

                  {editableEstimator.algorithm !== 'mlp' && (
                    <Alert severity="info" variant="outlined">
                      Choose Neural Network (MLP) to edit dense layers directly.
                    </Alert>
                  )}

                  {editableDenseLayers.map((units, index) => (
                    <Stack key={`${editableEstimator.id}-${index}`} direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label={`Layer ${index + 1}`} color="primary" variant="outlined" sx={{ minWidth: 72 }} />
                      <TextField
                        label="Units"
                        type="number"
                        size="small"
                        value={units}
                        onChange={(event) => setDenseLayerUnits(editableEstimator.id, index, event.target.value)}
                        inputProps={{ min: 1, step: 1 }}
                        sx={{ flex: 1 }}
                      />
                      <Tooltip title="Remove layer">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeDenseLayerAt(editableEstimator.id, index)}
                            disabled={editableDenseLayers.length <= 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Divider />
              {selectedNodeId && selectedNodeId !== 'input' && selectedNodeId !== 'output' && !selectedNodeId.endsWith('_ensemble') && !selectedNodeId.startsWith('model_layer_') && (
                <Button
                  startIcon={<DeleteIcon />}
                  color="error"
                  variant="outlined"
                  onClick={removeSelectedNode}
                  disabled={selectedNodeId === stack.singleEstimator.id || selectedNodeId === stack.metaLearner.id}
                  fullWidth
                >
                  Remove Selected Block
                </Button>
              )}

              <Divider />
              <Stack direction="row" spacing={1} alignItems="center">
                <InsightsIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">Selected Block</Typography>
                {(selectedNodeId || selectedCanvasNodeId) && (
                  <Tooltip title="Clear selection">
                    <IconButton size="small" onClick={() => {
                      setSelectedNodeId(null);
                      setSelectedCanvasNodeId(null);
                    }} sx={{ ml: 'auto' }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {!selectedNodeId && !selectedCanvasNodeId && (
                <Alert severity="info" variant="outlined">
                  Select a stack block on the canvas to tune its parameters.
                </Alert>
              )}

              {selectedCanvasNode && (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25, bgcolor: 'background.default' }}>
                  <Stack spacing={0.75}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2" sx={{ flex: 1 }} noWrap>
                        {selectedCanvasNode.data.label}
                      </Typography>
                      <Chip size="small" label={selectedCanvasNode.data.caption} variant="outlined" />
                    </Stack>
                    {selectedModelLayer && (
                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        <Chip size="small" label={`${selectedModelLayer.layer.units} units`} />
                        <Chip size="small" label={selectedModelLayer.layer.kind || 'layer'} variant="outlined" />
                        {selectedModelLayer.layer.detail && <Chip size="small" label={selectedModelLayer.layer.detail} variant="outlined" />}
                      </Stack>
                    )}
                    {(selectedCanvasNode.data.details || []).map((detail) => (
                      <Typography key={detail} variant="caption" color="text.secondary">
                        {detail}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}

              {selectedPreprocessing && (
                <Stack spacing={1.25}>
                  <TextField
                    label="Block label"
                    size="small"
                    value={selectedPreprocessing.label}
                    onChange={(event) =>
                      setStack((current) => ({
                        ...current,
                        preprocessing: current.preprocessing.map((step) =>
                          step.id === selectedPreprocessing.id ? { ...step, label: event.target.value } : step,
                        ),
                      }))
                    }
                    fullWidth
                  />
                  {Object.entries(selectedPreprocessing.params).map(([key, value]) => (
                    <TextField
                      key={key}
                      label={key}
                      size="small"
                      value={value}
                      onChange={(event) => setPreprocessingParam(selectedPreprocessing.id, key, event.target.value)}
                      fullWidth
                    />
                  ))}
                </Stack>
              )}

              {selectedEstimator && (
                <Stack spacing={1.25}>
                  <TextField
                    select
                    label="Algorithm"
                    size="small"
                    value={selectedEstimator.algorithm || ''}
                    displayEmpty
                    renderValue={(value) => value ? getAlgorithmLabel(String(value)) : 'Select algorithm'}
                    onChange={(event) => setEstimatorAlgorithm(selectedEstimator.id, event.target.value)}
                    fullWidth
                  >
                    <MenuItem value="" disabled>Select algorithm</MenuItem>
                    {visibleAlgorithms.map((algorithm) => (
                      <MenuItem key={algorithm.value} value={algorithm.value}>
                        {algorithm.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {Object.entries(selectedEstimator.params).map(([key, value]) => (
                    <TextField
                      key={key}
                      label={key}
                      size="small"
                      value={value}
                      onChange={(event) => setEstimatorParam(selectedEstimator.id, key, event.target.value)}
                      fullWidth
                    />
                  ))}
                </Stack>
              )}

              <Divider />
              <Stack direction="row" spacing={1} alignItems="center">
                <StorageIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" sx={{ flex: 1 }}>Feature Pipeline</Typography>
                <Button size="small" variant="outlined" onClick={() => switchCanvasView('pipeline')}>
                  View
                </Button>
              </Stack>
              {stack.preprocessing.length === 0 && (
                <Alert severity="info" variant="outlined">
                  Raw features flow directly into the model stack. Add transforms below to build the data pipeline.
                </Alert>
              )}
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {stack.preprocessing.map((step) => (
                  <Chip
                    key={step.id}
                    size="small"
                    label={step.label}
                    color={selectedNodeId === step.id ? 'primary' : 'default'}
                    variant={selectedNodeId === step.id ? 'filled' : 'outlined'}
                    onClick={() => {
                      setSelectedCanvasNodeId(step.id);
                      setSelectedNodeId(step.id);
                    }}
                    onDelete={() => {
                      setStack((current) => ({
                        ...current,
                        preprocessing: current.preprocessing.filter((item) => item.id !== step.id),
                      }));
                      if (selectedNodeId === step.id) setSelectedNodeId(null);
                      if (selectedCanvasNodeId === step.id) setSelectedCanvasNodeId(null);
                    }}
                  />
                ))}
              </Stack>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {PREPROCESSING_STEPS.map((step) => (
                  <Chip
                    key={step.value}
                    size="small"
                    label={step.label}
                    variant="outlined"
                    onClick={() => addPreprocessingStep(step.value)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>

            </Stack>
          </Box>
        </Paper>}
      </Box>
      {createAssetMutation.error && <ErrorAlert error={createAssetMutation.error as Error} />}
    </Box>
  );
};

export default ModelLabBuilderPage;
